import { PLATFORM_TIMEZONE } from "../../../utils/timezone";
import { useState, useMemo, useEffect, useRef } from "react";
import { Check, Filter, Plus, Search, Trash2, Upload } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import styles from "../../../styles/__lab_appointment_detail.module.scss";
import dialogStyles from "../../../styles/__labDialogAddEditTest.module.scss";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import { IoMdSettings } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import ServiceSearchSelect from "../../../components/service-search-select/ServiceSearchSelect";
import AmountInput from "../../../components/amount-input/AmountInput";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchDoctorAppointmentsReducer } from "../../../reducer/appointments/fetchDoctorAppointmentsReducer";
import { joinDoctorAppointmentReducer } from "../../../reducer/joinDoctorAppointment/joinDoctorAppointmentReducer";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import SlaTimer from "../../../components/sla-timer/SlaTimer";
import { FaCalendarAlt, FaClock, FaFileInvoice, FaWhatsapp, FaInfoCircle } from "react-icons/fa";

import { FiDownload, FiPrinter } from "react-icons/fi";
import {
  FaArrowLeftLong,
  FaCheck,
  FaCirclePlus,
  FaLocationArrow,
  FaPlus,
} from "react-icons/fa6";
import IconFileLargeView from "../../../assets/images/file-large-view.svg";
import IconPDF from "../../../assets/images/IconPDF.png";
import UserAvatar from "../../../assets/images/patientAvatar.png";
import { BsFillChatTextFill } from "react-icons/bs";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "bootstrap";
import { LiaMapMarkerSolid } from "react-icons/lia";
import Chat from "../../../components/chat/ChatModal";
import useRejectionReasons, {
  CUSTOM_REJECTION_REASON,
} from "../../../hooks/useRejectionReasons";

import { AiOutlineFilePdf } from "react-icons/ai";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import NurseDashboardLayout from "../../../components/nurse-dashboard-layout/NurseDashboardLayout";
import { IoIosCheckbox } from "react-icons/io";
import { TbLocationFilled } from "react-icons/tb";
import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import { LuCirclePlus } from "react-icons/lu";
import { RxChevronDown, RxClock } from "react-icons/rx";
import reportPreview from "../../../assets/images/reportPreview.png";
import { APP_PATH } from "../../../api/backendPath";
import { toast } from '../../../components/toast';
import axios from "axios";
import PhysioInvoiceModal from "../../../components/physio-invoice-modal/PhysioInvoiceModal";
import { composeBookingChildName } from "../../../utils/childBooking";
import { formatCustom as tzCustom, formatDateNumeric as tzDateNumeric, formatCustomInLocale as tzLocale, nowInDisplayZone } from "../../../utils/datetime";
import { formatPercent, formatWholeAmount } from "../../../utils/amountFormat";

// Canonical location codes for the physio order flow. Kept as constants so the
// values aren't hardcoded as magic strings throughout the component.
const LOCATION_CODES = {
  HOME_VISIT: "HOME_VISIT",
  ON_SITE: "ON_SITE",
};

// The backend/UI use a few different spellings for the same physical location
// (e.g. "Home Visit" / "HOME_VISIT" or "Visit Center" / "On-Site" / "ON_SITE" /
// "VISIT_CENTER"). Normalize any of them to a single canonical location code.
const normalizeLocationCode = (value) => {
  if (!value) return LOCATION_CODES.ON_SITE;
  const normalized = String(value).trim().toUpperCase().replace(/[\s-]+/g, "_");
  return normalized === "HOME_VISIT" || normalized === "HOMEVISIT"
    ? LOCATION_CODES.HOME_VISIT
    : LOCATION_CODES.ON_SITE;
};

// The provider's consultation type (configured by admin) constrains which test
// locations can be chosen: Online (video) → On-Site only, Home Visit (onsite) →
// Home Visit only, Both (video-onsite) → both. Unknown/empty → both (backward
// compatible so existing orders are never blocked).
const allowedLocationsFromConsultation = (type) => {
  const key = type ? String(type).trim().toLowerCase() : "";
  if (key === "video" || key === "online") return [LOCATION_CODES.ON_SITE];
  if (key === "onsite" || key === "home_visit" || key === "homevisit") return [LOCATION_CODES.HOME_VISIT];
  return [LOCATION_CODES.ON_SITE, LOCATION_CODES.HOME_VISIT];
};

// Formats an amount with thousand separators (e.g. 39999 -> "39,999").
// Accepts numbers, numeric strings, or already-comma-formatted strings and
// falls back to "0" for null/undefined/NaN so the price cell never shows
// "NaN" when a session row is missing a price.
const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  const num = parseFloat(String(value).replace(/,/g, ""));
  return isNaN(num) ? "0" : Math.round(num).toLocaleString("en-US");
};

const PhysioAppointmentDetail = ({ onLogout, onNavigateToDashboard }) => {
  // `i18n` was only needed by the inline invoice (RTL flag + print document lang); the shared
  // PhysioInvoiceModal reads it itself now.
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Order Details");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
  const [gender, setGender] = useState("male");
  const [showAddTherapyRow, setShowAddTherapyRow] = useState(false);

  // Order details state
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id");
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  // Results & Reports: true while a report PDF is uploading.
  const [uploadingResult, setUploadingResult] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Services catalog state (fetched from physio center's services)
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [newService, setNewService] = useState({ serviceName: "", numberOfSessions: 1, location: "ON_SITE", price: "" });
  // Provider's consultation type (configured by admin) — constrains test location options.
  const [consultationType, setConsultationType] = useState(null);
  const allowedLocations = allowedLocationsFromConsultation(consultationType);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editServiceData, setEditServiceData] = useState({});
  const [serviceDate, setServiceDate] = useState(null);
  const [serviceTime, setServiceTime] = useState("");
  const [validationErrors, setValidationErrors] = useState({ serviceDate: false, serviceTime: false });

  // Therapists list state
  const [therapistsList, setTherapistsList] = useState([]);
  const [selectedTherapistId, setSelectedTherapistId] = useState("");

  // Order-level transportation fee state
  const [orderTransportationFee, setOrderTransportationFee] = useState("0");
  const [transportationFeeAdded, setTransportationFeeAdded] = useState(false);
  const [showTransportationFeePopup, setShowTransportationFeePopup] = useState(false);
  const [tempTransportationFee, setTempTransportationFee] = useState("0");

  // Admin fee state
  const [adminFeePercentage, setAdminFeePercentage] = useState(0);

  // Assessment mode toggle
  const [assessmentMode, setAssessmentMode] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteServiceId, setDeleteServiceId] = useState(null);

  // Accept / Reject (first-received order) modal + loading state
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acceptingOrder, setAcceptingOrder] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(false);

  // Reject reason dropdown — admin-configured reasons for this center, plus an
  // "Other" option that reveals the free-text box. Fetched when the modal opens.
  const [selectedRejectReasonId, setSelectedRejectReasonId] = useState("");
  const {
    options: rejectReasonOptions,
    loading: loadingRejectReasons,
    resolveReasonText,
  } = useRejectionReasons("physiotherapy", showRejectModal);
  const isCustomRejectReason =
    selectedRejectReasonId === CUSTOM_REJECTION_REASON;

  // Single place to clear the dialog's inputs, used by every close path.
  const resetRejectForm = () => {
    setSelectedRejectReasonId("");
    setRejectReason("");
  };

  // Button loading/disabled states
  const [sendingTherapistLink, setSendingTherapistLink] = useState(false);
  const [therapistLinkSent, setTherapistLinkSent] = useState(false);
  const [collectingPayment, setCollectingPayment] = useState(false);

  // The whole order shares a single service location. Seed it from services that
  // are already added; otherwise fall back to the patient's preferred location.
  // (Split orders / per-service locations are no longer supported.)
  const getEffectiveLocation = () => {
    const existingWithLocation = (orderDetails?.services || []).find((s) => s?.location);
    let loc = existingWithLocation
      ? normalizeLocationCode(existingWithLocation.location)
      : normalizeLocationCode(orderDetails?.preferred_location);
    // Clamp to what the provider's consultation type permits.
    const allowed = allowedLocationsFromConsultation(consultationType);
    if (!allowed.includes(loc)) loc = allowed[0];
    return loc;
  };

  // Fetch the provider's consultation type once (constrains test locations).
  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const axiosInstance = axios.create({
          baseURL: process.env.REACT_APP_API_URL,
          headers: { Authorization: `Bearer ${token}` },
        });
        const res = await axiosInstance.get("/api/v1/physiotherapist/profile");
        const type = res?.data?.data?.consultation_type;
        if (type) setConsultationType(type);
        // The centre profile used to be stored here for the inline invoice header. The
        // shared PhysioInvoiceModal fetches its own profile, so nothing is kept now.
      } catch (e) {
        // Best-effort — on failure both locations remain allowed.
      }
    })();
  }, []);

  // When the "Add Therapy" row opens, default its location to the order's
  // effective location (the preferred location, or whatever the already-added
  // services use) instead of a hardcoded value.
  useEffect(() => {
    if (showAddTherapyRow) {
      setNewService((prev) => ({ ...prev, location: getEffectiveLocation() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddTherapyRow]);

  // ===== Physiotherapy center operating-hours slots =====
  // Slots for the selected service date come from
  // GET /api/v1/physiotherapist/center-slots (center resolved from the JWT).
  // Service-date-based slots — drives "Center Availability" + the Service Time
  // options (recomputed whenever the officer changes the service date).
  const [centerSlots, setCenterSlots] = useState(null);
  // Preferred-date-based slots — drives ONLY the Preferred Time Slot badge/range,
  // pinned to the patient's original preferred date (does NOT change with the
  // selected service date).
  const [preferredSlots, setPreferredSlots] = useState(null);
  // Signature of the availability banner the user dismissed, so it stays hidden
  // for that exact situation but re-appears when the reason/date changes.
  const [dismissedBannerKey, setDismissedBannerKey] = useState(null);
  // Whether the "no time slots for this date" tooltip is dismissed (reset when
  // the officer picks a different service date).
  const [serviceTimeTipDismissed, setServiceTimeTipDismissed] = useState(false);
  // True only when the officer actively picks a service date, so the "closed"
  // toast fires on selection but not on the initial page load.
  const dateSelectedByUserRef = useRef(false);

  // Period bands (minutes since midnight) matching the backend slot buckets.
  const PERIOD_BANDS = {
    MORNING: { start: 0, end: 12 * 60 },
    AFTERNOON: { start: 12 * 60, end: 17 * 60 },
    EVENING: { start: 17 * 60, end: 24 * 60 },
  };

  const hhmmToMinutes = (value) => {
    if (!value) return NaN;
    const [h, m] = String(value).split(":").map(Number);
    if (isNaN(h)) return NaN;
    return h * 60 + (isNaN(m) ? 0 : m);
  };

  const minutesToHHMM = (mins) =>
    `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

  const minutesToLabel12 = (mins) => {
    const norm = ((mins % 1440) + 1440) % 1440;
    const h24 = Math.floor(norm / 60);
    const m = norm % 60;
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };

  const formatDateYMD = (dateObj) => {
    if (!dateObj) return null;
    const y = dateObj.getFullYear();
    const mo = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  };

  // Parse a YYYY-MM-DD (or ISO) date string as a LOCAL calendar date so the day never
  // shifts with the browser's timezone. Null when it can't be read.
  const parseYMDLocal = (value) => {
    if (!value || value === "null") return null;
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  // The service can be on the patient's preferred date or any later day. The calendar
  // starts at the preferred date, or at today (Baghdad) once the preferred date has passed.
  const preferredServiceDate = parseYMDLocal(orderDetails?.preferred_date);
  const todayServiceDate = parseYMDLocal(nowInDisplayZone().date);
  const minServiceDate =
    preferredServiceDate && preferredServiceDate > todayServiceDate
      ? preferredServiceDate
      : todayServiceDate;
  const preferredDateLabel = preferredServiceDate
    ? format(preferredServiceDate, "dd/MM/yyyy")
    : "";
  // True when `d` is before the preferred date. The date already saved on the order is
  // accepted as-is (the backend applies the same rule).
  const isBeforePreferredDate = (d) =>
    !!(d && preferredServiceDate) &&
    formatDateYMD(d) < formatDateYMD(preferredServiceDate) &&
    formatDateYMD(d) !== String(orderDetails?.scheduled_date || "").slice(0, 10);
  // Toasts and scrolls to the field when the picked service date is before the preferred date.
  const blockServiceDateBeforePreferred = () => {
    if (!isBeforePreferredDate(serviceDate)) return false;
    toast.error(
      `Service date cannot be before the patient's preferred date (${preferredDateLabel}).`,
    );
    document
      .getElementById("service-date-field")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  };

  // Window for the patient's preferred period clipped to a given day's operating
  // hours (from a center-slots payload). Ends before the closing time (band end),
  // e.g. Morning + 8AM–1PM hours => 8:00 AM – 12:00 PM. Null when the day is
  // closed or the period doesn't overlap the operating hours.
  const getSlotWindowFrom = (slotsData) => {
    const slotKey = (orderDetails?.preferred_time_slot || "").toUpperCase();
    const band = PERIOD_BANDS[slotKey];
    if (!band) return null;
    if (!slotsData || slotsData.is_open === false) return null;
    const oh = slotsData.operating_hours;
    if (!oh) return null;
    const start = Math.max(band.start, hhmmToMinutes(oh.start));
    const end = Math.min(band.end, hhmmToMinutes(oh.end));
    if (!(start < end)) return null;
    return { startMin: start, endMin: end };
  };

  // Preferred-slot window pinned to the PREFERRED date (not affected by the
  // selected service date).
  const getPreferredSlotWindow = () => getSlotWindowFrom(preferredSlots);

  // Format a YYYY-MM-DD (or ISO) string as dd/MM/yyyy.
  const formatYMDLabel = (ymd) => {
    if (!ymd) return null;
    const parts = String(ymd).split("-");
    const d =
      parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
        : new Date(ymd);
    return isNaN(d.getTime()) ? null : tzLocale(d, "en-GB", undefined);
  };

  const preferredPeriodName = (() => {
    const slotKey = (orderDetails?.preferred_time_slot || "").toUpperCase();
    return slotKey === "MORNING" ? "Morning"
      : slotKey === "AFTERNOON" ? "Afternoon"
      : slotKey === "EVENING" ? "Evening"
      : "preferred";
  })();

  const hoursTextFrom = (oh) =>
    oh && oh.start && oh.end
      ? `${minutesToLabel12(hhmmToMinutes(oh.start))} - ${minutesToLabel12(hhmmToMinutes(oh.end))}`
      : null;

  // Single source of truth for the preferred-slot availability (drives both the
  // Open/Closed dot and the out-of-hours banner). Returns whether the center is
  // open for the patient's preferred period on the preferred date, plus the
  // reason it's closed:
  //  - CLOSED_DAY  : the center is closed on the preferred date.
  //  - OUT_OF_HOURS: the preferred period is outside that day's operating hours.
  //  - PAST        : the preferred date is today and the operating hours ended.
  const evaluatePreferredSlotStatus = () => {
    if (!preferredSlots) return { loaded: false, open: false, reason: null };
    if (preferredSlots.is_open !== true)
      return { loaded: true, open: false, reason: "CLOSED_DAY" };
    const win = getPreferredSlotWindow();
    if (!win) return { loaded: true, open: false, reason: "OUT_OF_HOURS" };
    const todayStr = nowInDisplayZone().date; // Baghdad date
    if (preferredSlots.date === todayStr) {
      const closeMin = hhmmToMinutes(preferredSlots?.operating_hours?.end);
      const nowMinutes = nowInDisplayZone().minutes; // Baghdad time
      if (!isNaN(closeMin) && nowMinutes >= closeMin)
        return { loaded: true, open: false, reason: "PAST" };
    }
    return { loaded: true, open: true, reason: null };
  };

  // Human-readable banner content (title + detail) explaining why the preferred
  // slot is closed, referencing the patient's preferred slot, the date, and the
  // center's operating hours so the officer understands it's out of time.
  const getPreferredSlotClosedMessage = (reason) => {
    const dateLabel = formatYMDLabel(orderDetails?.preferred_date);
    const onDate = dateLabel ? ` on ${dateLabel}` : "";
    const hoursText = hoursTextFrom(preferredSlots?.operating_hours);
    if (reason === "CLOSED_DAY")
      return {
        title: "Center closed on the preferred date",
        detail: `The center is closed${onDate}, so the patient's preferred ${preferredPeriodName} slot can't be scheduled that day.`,
      };
    if (reason === "PAST")
      return {
        title: "Preferred time has already passed",
        detail: `The ${preferredPeriodName} slot${onDate} has already passed for today${hoursText ? `. The center's operating hours are ${hoursText}` : ""}.`,
      };
    // OUT_OF_HOURS
    return {
      title: "Preferred time is outside operating hours",
      detail: `The patient's preferred ${preferredPeriodName} slot${onDate} falls outside the center's operating hours${hoursText ? ` (${hoursText})` : ""}.`,
    };
  };

  // Build the availability banner content. Prioritizes the SELECTED SERVICE DATE
  // (the actionable choice) once its slots have loaded — warning when that date
  // is closed or the preferred period falls outside its operating hours — and
  // otherwise falls back to the patient's PREFERRED slot status. Returns
  // `{ key, title, detail }` or null when nothing needs flagging. `key` is a
  // stable signature used to reset the dismiss state when the reason changes.
  const buildSlotBanner = () => {
    const selStr = formatDateYMD(serviceDate);
    if (serviceDate && centerSlots && centerSlots.date === selStr) {
      const dateLabel = formatYMDLabel(selStr);
      if (centerSlots.is_open === false) {
        return {
          key: `SVC|CLOSED|${selStr}`,
          title: "Center closed on the selected service date",
          detail: `The center is closed on ${dateLabel}. Please choose another service date.`,
        };
      }
      if (!getSlotWindowFrom(centerSlots)) {
        const hoursText = hoursTextFrom(centerSlots.operating_hours);
        return {
          key: `SVC|OOH|${selStr}`,
          title: "Selected service date is outside operating hours",
          detail: `The ${preferredPeriodName} slot on ${dateLabel} falls outside the center's operating hours${hoursText ? ` (${hoursText})` : ""}. Please choose another date or time.`,
        };
      }
      return null; // service date is valid → no banner
    }

    // No service date chosen yet → reflect the patient's preferred slot.
    const pref = evaluatePreferredSlotStatus();
    if (pref.loaded && !pref.open) {
      const msg = getPreferredSlotClosedMessage(pref.reason);
      return {
        key: `PREF|${pref.reason}|${orderDetails?.preferred_date}`,
        title: msg.title,
        detail: msg.detail,
      };
    }
    return null;
  };

  // Low-level call to the center-slots endpoint for a given date (center = auth user).
  const requestCenterSlots = async (dateStr) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = `/api/v1/physiotherapist/center-slots${dateStr ? `?date=${dateStr}` : ""}`;
      const res = await axiosInstance.get(url);
      return res?.data?.data || null;
    } catch (e) {
      return null;
    }
  };

  // Service-date-based fetch (Center Availability + Service Time). When
  // notifyIfClosed is true (officer picked the date), a closed day shows a toast.
  const fetchCenterSlots = async (dateStr, notifyIfClosed = false) => {
    const data = await requestCenterSlots(dateStr);
    if (data) {
      setCenterSlots(data);
      if (notifyIfClosed && data.is_open === false) {
        toast.error("The center is closed on the selected date. Please choose another date.");
      }
    }
  };

  // On load and whenever the service date changes, refresh the service-date slots.
  useEffect(() => {
    const dateStr = formatDateYMD(serviceDate) || orderDetails?.preferred_date || null;
    if (dateStr) {
      const notify = dateSelectedByUserRef.current;
      dateSelectedByUserRef.current = false;
      fetchCenterSlots(dateStr, notify);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDate, orderDetails?.preferred_date]);

  // Fetch the preferred-date slots ONCE (pinned to the patient's preferred date)
  // so the Preferred Time Slot badge/range never changes with the service date.
  useEffect(() => {
    const pd = orderDetails?.preferred_date || null;
    if (pd) requestCenterSlots(pd).then((data) => { if (data) setPreferredSlots(data); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails?.preferred_date]);

  // Get time range based on preferred time slot
  const getTimeRangeForSlot = (timeSlot) => {
    // Prefer the live operating-hours window for the preferred period.
    const win = getPreferredSlotWindow();
    if (win) return { min: minutesToHHMM(win.startMin), max: minutesToHHMM(win.endMin) };
    if (!timeSlot) return { min: "00:00", max: "23:59" };
    const slot = timeSlot.toUpperCase();
    if (slot.includes('MORNING')) return { min: "08:00", max: "12:00" };
    if (slot.includes('AFTERNOON')) return { min: "12:00", max: "17:00" };
    if (slot.includes('EVENING')) return { min: "17:00", max: "19:30" };
    return { min: "00:00", max: "23:59" };
  };

  // Generate time options based on time slot
  const generateTimeOptions = (timeSlot) => {
    const timeRange = getTimeRangeForSlot(timeSlot);
    const options = [];
    const [minHour, minMinute] = timeRange.min.split(':').map(Number);
    const [maxHour, maxMinute] = timeRange.max.split(':').map(Number);
    for (let hour = minHour; hour <= maxHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === minHour && minute < minMinute) continue;
        if (hour === maxHour && minute > maxMinute) break;
        if (hour > maxHour) break;
        const timeValue = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayTime = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
        options.push({ value: timeValue, label: displayTime });
      }
    }
    return options;
  };

  // Turn a "HH:MM" slot into a { value, label } option with a 12-hour label.
  const slotToOption = (hhmm) => {
    const [h, m] = String(hhmm).split(":").map(Number);
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h >= 12 ? "PM" : "AM";
    return {
      value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      label: `${displayHour}:${String(m).padStart(2, "0")} ${period}`,
    };
  };

  // Service Time options come from the center-slots endpoint for the selected
  // service date (the patient's preferred period bucket — already clipped to
  // that day's operating hours). Falls back to the static generator until the
  // slots load; a closed day / preferred period with no slots yields no options.
  const buildTimeOptions = () => {
    const slotKey = (orderDetails?.preferred_time_slot || "").toLowerCase();
    const bucket = centerSlots?.slots?.[slotKey];
    if (centerSlots) {
      let slots = Array.isArray(bucket?.slots) ? bucket.slots : [];
      // If the selected date is today, drop slots whose start time has already
      // passed (e.g. the morning slots are over by the afternoon → empty list).
      const todayStr = nowInDisplayZone().date; // Baghdad date
      if (centerSlots.date && centerSlots.date === todayStr) {
        const nowMinutes = nowInDisplayZone().minutes; // Baghdad time
        slots = slots.filter((hhmm) => hhmmToMinutes(hhmm) > nowMinutes);
      }
      return slots.map(slotToOption);
    }
    // Not loaded yet — fall back to the previous static generation.
    return generateTimeOptions(orderDetails?.preferred_time_slot);
  };

  const timeOptions = buildTimeOptions();

  // Fetch order details
  const fetchOrderDetails = async () => {
    if (!orderId) return;
    setOrderLoading(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const response = await axiosInstance.get(`${APP_PATH.physioOrders}/${orderId}`);
      if (response?.data?.status === true || response?.status === 200) {
        const data = response.data.data;
        setOrderDetails(data);
        // Parse as a local date (also handles an ISO timestamp) so the saved service
        // date always shows in the Service Date input.
        const parsedDate = parseYMDLocal(data.scheduled_date);
        if (parsedDate) {
          setServiceDate(parsedDate);
        }
        if (data.scheduled_time && data.scheduled_time !== 'null') {
          // Normalize time to HH:MM format (strip seconds if present, e.g. "10:00:00" → "10:00")
          const timeParts = data.scheduled_time.split(':');
          const normalizedTime = timeParts.length >= 2 ? `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}` : data.scheduled_time;
          setServiceTime(normalizedTime);
        }
        // Initialize transportation fee from order data
        if (data.transportation_fee && Number(data.transportation_fee) > 0) {
          setOrderTransportationFee(String(Math.round(Number(data.transportation_fee))));
          setTransportationFeeAdded(true);
        }
        // Initialize admin fee percentage from order data if available
        if (data.admin_fee_percentage && Number(data.admin_fee_percentage) > 0) {
          setAdminFeePercentage(Number(data.admin_fee_percentage));
        }
        // Initialize assessment mode from order data
        if (data.assessment_required === true || data.assessment_required === 'true') {
          setAssessmentMode(true);
        }
      } else {
        toast.error(response?.data?.message || "Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setOrderLoading(false);
    }
  };

  // ===== Results & Reports: upload / view / delete report PDFs =====
  // One PDF per change event; upload repeatedly to attach multiple reports.
  const handleUploadResults = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File size must be less than 25MB");
      return;
    }
    if (!orderId) {
      toast.error("No order ID available");
      return;
    }
    if (uploadingResult) {
      toast.warning("Upload already in progress");
      return;
    }
    setUploadingResult(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const formData = new FormData();
      formData.append("file", file);
      const fileSizeMB = file.size / (1024 * 1024);
      const endpoint =
        fileSizeMB > 10
          ? `${process.env.REACT_APP_API_URL}${APP_PATH.physioOrders}/${orderId}/upload-results-multipart`
          : `${process.env.REACT_APP_API_URL}${APP_PATH.physioOrders}/${orderId}/upload-results`;
      const res = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.data?.status) {
        toast.success("Report uploaded successfully");
        fetchOrderDetails();
      } else {
        toast.error(res.data?.message || "Failed to upload report");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to upload report");
    } finally {
      setUploadingResult(false);
    }
  };

  const handleResultFileClick = async (fileId) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      toast.info("Opening file...");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.physioOrders}/${orderId}/file/${fileId}/presigned-url`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await res.json();
      if (result.status && result.data?.presignedUrl) {
        window.open(result.data.presignedUrl, "_blank");
      } else {
        toast.error(result.message || "Failed to open file");
      }
    } catch (e) {
      toast.error("Failed to open file");
    }
  };

  const handleResultFileDelete = async (fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.physioOrders}/${orderId}/file/${fileId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await res.json();
      if (result.status) {
        toast.success("File deleted");
        fetchOrderDetails();
      } else {
        toast.error(result.message || "Failed to delete file");
      }
    } catch (e) {
      toast.error("Failed to delete file");
    }
  };

  // Share the uploaded report(s) with the patient. Once shared, upload/delete/
  // share are locked (report_status = SENT) — mirrors the imaging flow.
  const handleShareWithPatient = async () => {
    if (!orderId) {
      toast.error("No order ID available");
      return;
    }
    if (String(orderDetails?.report_status || "").toUpperCase() === "SENT") {
      toast.info("Report has already been shared with the patient");
      return;
    }
    if (testResults.length === 0) {
      toast.error("Upload at least one report before sharing");
      return;
    }
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axiosInstance.post(
        `${APP_PATH.physioOrders}/${orderId}/share-report`,
      );
      if (res.data?.status) {
        toast.success("Report shared with patient successfully");
        fetchOrderDetails();
      } else {
        toast.error(res.data?.message || "Failed to share report");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to share report");
    }
  };

  // Fetch services catalog for this physio center
  const fetchServicesCatalog = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.get(`${APP_PATH.physioServices}?limit=100`);
      if (response?.data?.data) {
        const data = response.data.data;
        const services = data.services || (Array.isArray(data) ? data : []);
        setServicesCatalog(services);
      } else if (response?.data?.status && Array.isArray(response.data.services)) {
        setServicesCatalog(response.data.services);
      }
    } catch (error) {
      console.error("Error fetching services catalog:", error);
    }
  };

  // Fetch therapists for this physio center
  const fetchTherapists = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.get(`${APP_PATH.getTherapists}?limit=100&status=ACTIVE`);
      if (response?.data?.status && response.data.data) {
        const data = response.data.data;
        const therapists = data.therapists || (Array.isArray(data) ? data : []);
        setTherapistsList(therapists);
      }
    } catch (error) {
      console.error("Error fetching therapists:", error);
    }
  };

  // Assign therapist to the order
  const handleAssignTherapist = async () => {
    if (!orderId) {
      toast.error("No order ID available");
      return;
    }
    if (orderDetails?.assigned_therapist_id) {
      toast.info("A therapist is already assigned to this order");
      return;
    }
    if (!selectedTherapistId) {
      toast.error(
        t(
          "physioAppointmentDetail.selectTherapistBeforeProceeding",
          "Please select a therapist before proceeding.",
        ),
      );
      return;
    }
    if (blockServiceDateBeforePreferred()) return;
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axiosInstance.put(`${APP_PATH.physioOrders}/${orderId}/assign-therapist`, {
        therapistId: Number(selectedTherapistId),
        scheduledDate: serviceDate instanceof Date ? `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}-${String(serviceDate.getDate()).padStart(2, '0')}` : (serviceDate || null),
        scheduledTime: serviceTime || null,
        transportationFee: transportationFeeAdded ? Number(orderTransportationFee) : (Number(orderDetails?.transportation_fee) || 0),
        adminFeePercentage: adminFeePercentage || Number(orderDetails?.admin_fee_percentage) || 0,
      });
      if (res.data?.status) {
        toast.success("Therapist assigned successfully");
        fetchOrderDetails();
      } else {
        toast.error(res.data?.message || "Failed to assign therapist");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to assign therapist");
    }
  };

  // Fetch physio service fees configuration (admin fee percentage)
  const fetchPhysioServiceFees = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.get(APP_PATH.physioFeesConfiguration);
      if (response?.data?.status && response.data.data) {
        const data = response.data.data;
        // Set admin fee percentage - check if active or default to using the value if present
        if (data.admin_fee && data.admin_fee.percentage !== undefined && data.admin_fee.is_active !== false) {
          const adminPercentage = parseFloat(data.admin_fee.percentage);
          if (!isNaN(adminPercentage) && adminPercentage > 0) {
            setAdminFeePercentage(adminPercentage);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching physio service fees:", error);
    }
  };

  // Add a new service to the order
  const handleAddService = async () => {
    if (!newService.serviceName || !newService.price) {
      toast.error("Please select a service and enter a price");
      return;
    }
    // Check for duplicates
    const isDuplicate = (orderDetails?.services || []).some(s => s.service_name === newService.serviceName);
    if (isDuplicate) {
      toast.error("This service is already added to the order");
      return;
    }

    // The whole order shares a single location. Whatever location is selected
    // for the new service is applied to every service in the order (no split
    // orders / per-service locations, so there is no conflict to resolve).
    const finalLocation = normalizeLocationCode(newService.location);

    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      // Get existing services and add the new one - all share the same location.
      const existingServices = (orderDetails?.services || []).map(s => ({
        serviceName: s.service_name,
        serviceCode: s.service_code,
        price: Number(s.price),
        location: finalLocation,
        numberOfSessions: s.number_of_sessions || 1,
      }));
      const allServices = [...existingServices, {
        serviceName: newService.serviceName,
        serviceCode: newService.serviceCode || "",
        price: Number(newService.price),
        location: finalLocation,
        numberOfSessions: Number(newService.numberOfSessions) || 1,
      }];
      const response = await axiosInstance.post(`${APP_PATH.physioOrders}/${orderId}/add-services`, {
        services: allServices,
        transportationFee: transportationFeeAdded ? orderTransportationFee : (orderDetails?.transportation_fee || 0),
      });
      if (response?.data?.status) {
        toast.success("Service added successfully");
        setShowAddTherapyRow(false);
        setNewService({ serviceName: "", numberOfSessions: 1, location: getEffectiveLocation(), price: "" });
        fetchOrderDetails();
      } else {
        console.error("Add service failed:", response?.data);
        toast.error(response?.data?.message || response?.data?.error || "Failed to add service");
      }
    } catch (error) {
      console.error("Add service error:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || error?.response?.data?.error || "Failed to add service");
    }
  };

  // Delete a service from the order
  const handleDeleteService = async (serviceId) => {
    setDeleteServiceId(serviceId);
    setShowDeleteModal(true);
  };

  // Confirm delete service
  const confirmDeleteService = async () => {
    const serviceId = deleteServiceId;
    setShowDeleteModal(false);
    setDeleteServiceId(null);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.delete(`${APP_PATH.physioOrders}/services/${serviceId}`);
      if (response?.data?.status) {
        toast.success("Service removed successfully");
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to remove service");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to remove service");
    }
  };

  // Save edited service. The whole order shares a single location, so if the
  // edited service's location changed, every other service is synced to match
  // (no split orders / per-service locations).
  const handleSaveEdit = async (serviceId) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const newLocation = normalizeLocationCode(editServiceData.location);
      const response = await axiosInstance.put(`${APP_PATH.physioOrders}/services/${serviceId}`, {
        numberOfSessions: Number(editServiceData.number_of_sessions),
        price: Number(editServiceData.price),
        location: newLocation,
      });
      if (response?.data?.status) {
        // Sync the location of every other service in the order to match.
        const siblings = (orderDetails?.services || []).filter(
          (s) => String(s.id) !== String(serviceId) && normalizeLocationCode(s.location) !== newLocation
        );
        if (siblings.length > 0) {
          await Promise.all(
            siblings.map((s) =>
              axiosInstance.put(`${APP_PATH.physioOrders}/services/${s.id}`, {
                numberOfSessions: Number(s.number_of_sessions) || 1,
                price: Number(s.price),
                location: newLocation,
              })
            )
          );
        }
        toast.success("Service updated successfully");
        setEditingServiceId(null);
        setEditServiceData({});
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to update service");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update service");
    }
  };

  // Handle Send Invoice to Patient with validation
  const handleSendInvoice = async () => {
    // Reset validation errors
    setValidationErrors({ serviceDate: false, serviceTime: false });

    // Validate services exist
    if (!orderDetails?.services || orderDetails.services.length === 0) {
      toast.error("Please add at least one service before sending the invoice");
      return;
    }

    // Validate service date
    if (!serviceDate || !(serviceDate instanceof Date) || isNaN(serviceDate.getTime())) {
      setValidationErrors(prev => ({ ...prev, serviceDate: true }));
      toast.error("Please select a service date before sending the invoice");
      // Scroll to the service date field
      document.getElementById('service-date-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (blockServiceDateBeforePreferred()) return;

    // Validate service time
    if (!serviceTime || serviceTime.trim() === "" || serviceTime === "Select time") {
      setValidationErrors(prev => ({ ...prev, serviceTime: true }));
      toast.error("Please select a service time before sending the invoice");
      // Scroll to the service time field
      document.getElementById('service-time-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // NOTE: the service time no longer has to fall inside the preferred time
    // slot's window — the center can schedule at any time (validation removed).

    // All validations passed - proceed with sending invoice
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });

      const formattedDate = `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}-${String(serviceDate.getDate()).padStart(2, '0')}`;
      // The centre works on Baghdad time (fixed), whatever this device's clock says.
      const timezone = PLATFORM_TIMEZONE;

      const response = await axiosInstance.post(`${APP_PATH.physioOrders}/${orderId}/send-invoice`, {
        scheduledDate: formattedDate,
        scheduledTime: serviceTime,
        timezone: timezone,
        transportationFee: transportationFeeAdded ? Number(orderTransportationFee) : Number(orderDetails?.transportation_fee || 0),
        adminFeePercentage: adminFeePercentage || 0,
      });

      if (response?.data?.status) {
        toast.success("Invoice sent to patient successfully");
        navigate("/physio_ordermanagement");
      } else {
        toast.error(response?.data?.message || "Failed to send invoice");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send invoice");
    }
  };

  // Accept a newly-received order. Keeps status REQUEST_SUBMITTED (backend only
  // flips the physio_accepted flag) and notifies the patient, then the officer
  // can add therapies + send the invoice. Confirmed via a dialog.
  const confirmAcceptOrder = async () => {
    setAcceptingOrder(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.post(`${APP_PATH.physioOrders}/${orderId}/accept`);
      if (response?.data?.status) {
        toast.success("Order accepted");
        setShowAcceptModal(false);
        fetchOrderDetails();
      } else {
        toast.error(response?.data?.message || "Failed to accept order");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to accept order");
    } finally {
      setAcceptingOrder(false);
    }
  };

  // Reject a newly-received order. Sets status REJECTED and notifies the patient
  // (bilingual, persisted) with the optional reason. Confirmed via a dialog.
  const confirmRejectOrder = async () => {
    // A preset reason submits its own text; "Other" submits the free-text box.
    const presetReason = resolveReasonText(selectedRejectReasonId);
    const finalReason = presetReason || rejectReason.trim();

    setRejectingOrder(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${token}` },
      });
      const response = await axiosInstance.post(`${APP_PATH.physioOrders}/${orderId}/reject`, {
        rejectionReason: finalReason || undefined,
      });
      if (response?.data?.status) {
        toast.success("Order rejected");
        setShowRejectModal(false);
        resetRejectForm();
        navigate("/physio_ordermanagement");
      } else {
        toast.error(response?.data?.message || "Failed to reject order");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to reject order");
    } finally {
      setRejectingOrder(false);
    }
  };

  const dispatch = useDispatch();
  const {
    data: apiAppointments,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.fetchDoctorAppointments);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const toggleChat = () => {
    setIsChatOpen((prev) => !prev);
  };

  // Use Redux state for totalPages, fallback to local state if needed
  const totalPages = reduxTotalPages || 1;
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  // Static placeholder appointments (kept for reference)

  // Bind appointments from API (normalize shape for the existing UI)
  const rawData = Array.isArray(apiAppointments) ? apiAppointments : (apiAppointments?.orders || apiAppointments?.data?.orders || []);
  const appointments = rawData.map((a) => {
    const patient = a?.patient || {};
    const name = `${patient?.full_name || ""} ${
      patient?.surname || ""
    }`.trim();
    const avatar = patient?.avatar_presigned_url || patient?.avatar || user;
    // Determine typeIcon based on consultation_type
    const typeIcon =
      a.consultation_type === "video"
        ? "video"
        : a.consultation_type === "audio"
          ? "mic"
          : "message";
    const type = a.consultation_type === "video" ? "Online Consult" : "On-Site";
    return {
      id: a.id,
      name,
      avatar,
      location: a?.location || a?.onsite_details || "",
      date: a?.appointment_date || a?.booking_date || "",
      time:
        a?.formatted_time ||
        (a?.appointment_time_slot
          ? a.appointment_time_slot.charAt(0).toUpperCase() +
            a.appointment_time_slot.slice(1)
          : ""),
      typeIcon,
      type,
      status: a?.status || "",
      raw: a,
    };
  });

  // Sort appointments (filtering is done server-side)
  const sortedAppointments = useMemo(() => {
    // Sort appointments
    const sorted = [...appointments].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [appointments, sortField, sortDirection]);

  // Pagination
  // const totalPages = Math.ceil(
  //   filteredAndSortedAppointments.length / itemsPerPage
  // );
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const paginatedAppointments = filteredAndSortedAppointments.slice(
  //   startIndex,
  //   startIndex + itemsPerPage
  // );

  const paginatedAppointments = sortedAppointments;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return null;
    try {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Format status text for display
  const formatStatusText = (status) => {
    if (!status) return "";
    // Convert to display format
    const statusDisplayMap = {
      pending: "Pending",
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed",
      cancelled: "Cancelled",
      reschedule_requested: "Reschedule Requested",
      "reschedule-requested": "Reschedule Requested",
    };

    const lowerStatus = status.toLowerCase();
    if (statusDisplayMap[lowerStatus]) {
      return statusDisplayMap[lowerStatus];
    }

    // Capitalize first letter of each word for other statuses
    return status
      .split(/[_\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;
    // Convert display format to API format
    const statusMap = {
      Pending: "pending",
      Upcoming: "upcoming",
      Ongoing: "ongoing",
      Completed: "completed",
      Cancelled: "cancelled",
      "Reschedule Requested": "reschedule_requested",
      reschedule_requested: "reschedule_requested",
      pending: "pending",
      upcoming: "upcoming",
      ongoing: "ongoing",
      completed: "completed",
      cancelled: "cancelled",
    };
    return statusMap[status] || status.toLowerCase().replace(/\s+/g, "_");
  };

  // Function to fetch appointments with current filters
  // Optional parameters allow overriding filter values (useful for tab clicks and reset)
  // Use undefined as default to distinguish between "not provided" and "explicitly null"
  const fetchAppointmentsWithFilters = (
    pageNum = currentPage,
    statusOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
  ) => {
    // Use override values if provided (including null), otherwise use current state
    // undefined means "use state value", null means "explicitly clear/no filter"
    const dateToUse = dateOverride !== undefined ? dateOverride : date;
    const statusToUse =
      statusOverride !== undefined ? statusOverride : statusFilter;
    const searchToUse =
      searchOverride !== undefined ? searchOverride : searchTerm;

    const formattedDate = formatDate(dateToUse);
    const normalizedStatus = normalizeStatusForAPI(statusToUse);

    // Build params object - only include defined values
    const params = {
      page: pageNum,
      limit: itemsPerPage,
    };

    // Only add date if it's a valid formatted date string (not null/undefined/empty)
    if (
      formattedDate &&
      formattedDate !== null &&
      formattedDate !== "null" &&
      formattedDate !== ""
    ) {
      params.date = formattedDate;
    }

    if (searchToUse && searchToUse.trim()) {
      params.search = searchToUse.trim();
    }

    if (normalizedStatus) {
      params.status = normalizedStatus;
    }

    dispatch(fetchDoctorAppointmentsReducer(params))
      .then((res) => {
        setCurrentPage(pageNum);
        // Total pages will be updated in Redux state automatically
      })
      .catch((error) => {
        console.error("❌ Error fetching appointments:", error);
      });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchAppointmentsWithFilters(page);
  };

  const resetFilters = () => {
    // Reset state values first
    setSearchTerm("");
    setStatusFilter("All");
    setDate(null);
    setCurrentPage(1);
    setActiveTab("All"); // Also reset the active tab

    // Immediately fetch with reset values (pass them directly to avoid async state update issue)
    // Explicitly pass null for date to ensure it's cleared
    fetchAppointmentsWithFilters(1, "All", null, "");
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  const joinAppointment = async (id) => {
    try {
      const result = await dispatch(
        joinDoctorAppointmentReducer({ appointmentId: id }),
      ).unwrap();
      console.log("result:", result);
      // reducer returns response.data.data (normalized) so join_url should be on result
      const joinUrl = result?.join_url || result?.data?.join_url;
      if (joinUrl) {
        // open in a new tab
        window.open(joinUrl, "_blank");
      } else {
        console.warn(
          "No join_url received from joinDoctorAppointmentReducer",
          result,
        );
      }
    } catch (err) {
      console.error("Failed to join appointment:", err);
    }
  };

  // Initial load with default filters
  useEffect(() => {
    fetchAppointmentsWithFilters(1);
    fetchOrderDetails();
    fetchServicesCatalog();
    fetchTherapists();
    fetchPhysioServiceFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // useEffect(() => {
  //   dispatch(fetchDoctorAppointmentsReducer()).then((res)=>{
  //   setTotalPages(res?.payload?.pagination?.total_pages)
  // })

  //   // dispatch(joinDoctorAppointmentReducer({appointmentId:2}))
  // }, []);

  const handlePageChange1 = (data) => {
    const nextPage = data + 1;
    fetchAppointmentsWithFilters(nextPage);
  };

  const handlePageChange11 = (data) => {
    const prevPage = data - 1;
    if (prevPage >= 1) {
      fetchAppointmentsWithFilters(prevPage);
    }
  };
  const handleBackDetail = (state, data) => {
    setShowDetails(state);
    setSelectedAppointment(data);
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const CreateFollowUp = (data) => {
    setOpenDialogCreateFollowUp(true);
    setSelectedAppointment(data);
  };

  // Determine if Therapist Detail / Processing tab should be shown
  const invoiceSent = orderDetails?.status && !['REQUEST_SUBMITTED'].includes(orderDetails?.status);
  const isCODPayment = orderDetails?.payment_method?.toUpperCase() === 'COD';
  const isPaymentPaid = orderDetails?.payment_status === 'PAID' || orderDetails?.payment_status === 'COMPLETED';
  const isOrderCompleted = orderDetails?.status === 'COMPLETED';
  const isOrderAccepted = orderDetails?.status === 'ORDER_ACCEPTED';
  // After split decision is confirmed by patient, services are locked
  const isPostSplitConfirmation = orderDetails?.patient_confirmation_received === true && orderDetails?.split_decision;
  // A newly-received order must be Accepted by the center before therapies can be
  // added / invoice sent. Backend keeps status REQUEST_SUBMITTED and flips this flag.
  const isPhysioAccepted = orderDetails?.physio_accepted === true;
  // First-received state: show Accept/Reject instead of the add-test + send-invoice UI.
  const isAwaitingCenterDecision =
    orderDetails?.status === 'REQUEST_SUBMITTED' && !isPhysioAccepted && !isPostSplitConfirmation;
  // Hide the Therapy Details + Invoice sections until the order is accepted. While
  // awaiting the center's decision only the Accept/Reject buttons show; if the
  // order is Rejected the sections stay hidden. Once accepted (or the order has
  // progressed past the decision step) the sections appear.
  const isOrderRejected = orderDetails?.status === 'REJECTED';
  // Reason the officer gave when rejecting, shown in the "Order rejected" panel.
  const orderRejectionReason =
    orderDetails?.rejection_reason || orderDetails?.rejectionReason || '';
  const showTherapyAndInvoiceSections =
    !!orderDetails && !isAwaitingCenterDecision && !isOrderRejected;
  // The officer can only set the Service Date / Time once the order has been
  // accepted. Both inputs are locked while the order is still awaiting the
  // Accept/Reject decision, was rejected, or is in ASSESSMENT (the schedule is
  // driven by the assessment/session flow at that point).
  const scheduleInputsLocked =
    isAwaitingCenterDecision || isOrderRejected || orderDetails?.status === 'ASSESSMENT';
  // The invoice has been sent to the patient: the order is waiting for payment
  // (first session or a revisit) or has moved past it. From here on the service
  // date / time are fixed, so the operating-hours warnings (closed / outside
  // hours / no time slots) are no longer shown, even if the center later changes
  // its operating hours. REQUEST_SUBMITTED / ASSESSMENT are still before it.
  const invoiceSentStatuses = [
    'AWAITING_PAYMENT',
    'ORDER_ACCEPTED',
    'SCHEDULED',
    'THERAPIST_ASSIGNED',
    'THERAPIST_DISPATCHED',
    'IN_PROGRESS',
    'COMPLETED',
    'REVISIT_REQUESTED',
  ];
  const isInvoiceSentToPatient = invoiceSentStatuses.includes(orderDetails?.status);
  // The service date / time can no longer change: the invoice was sent, or the
  // order is closed as REJECTED (by the center, or automatically when the patient
  // did not pay within the payment window after the invoice was sent). The
  // operating-hours warnings ask the officer to pick another date / time, which
  // is not possible here, so they stay hidden even if the hours change later.
  const isServiceScheduleFixed = isInvoiceSentToPatient || isOrderRejected;
  // Invoice sent and still waiting for the patient to pay, shown in the blue
  // "Invoice sent" panel at the top of the Order Details tab. A revisit sends the
  // next session's request, which the patient also has to pay.
  const isRevisitAwaitingPayment =
    orderDetails?.status === 'REVISIT_REQUESTED' && !isPaymentPaid;
  const isAwaitingPatientPayment =
    (orderDetails?.status === 'AWAITING_PAYMENT' && !isPaymentPaid) ||
    isRevisitAwaitingPayment;
  // Show therapist tab when order is accepted AND (COD or payment done), OR when status is ASSESSMENT (for both home visit and on-site)
  const isAssessmentStatus = orderDetails?.status === 'ASSESSMENT';

  // The order's ACTUAL service location is driven by the services' location — it
  // can be changed to On-Site independently of the patient's original
  // preferred_location (which may still say Home Visit). Mirror the "Service
  // Location" field: any Home Visit service ⇒ Home Visit order, otherwise
  // On-Site. Fall back to preferred_location only when no services exist yet.
  const orderServicesList = orderDetails?.services || [];
  const isHomeVisitService =
    orderServicesList.length > 0
      ? orderServicesList.some(
          (s) => normalizeLocationCode(s.location) === LOCATION_CODES.HOME_VISIT,
        )
      : normalizeLocationCode(orderDetails?.preferred_location) ===
        LOCATION_CODES.HOME_VISIT;
  const isOnSiteService = !isHomeVisitService;

  const showTherapistTab =
    isAssessmentStatus ||
    ((isOrderAccepted ||
      orderDetails?.status === 'SCHEDULED' ||
      orderDetails?.status === 'THERAPIST_ASSIGNED' ||
      orderDetails?.status === 'THERAPIST_DISPATCHED' ||
      orderDetails?.status === 'IN_PROGRESS' ||
      isOrderCompleted) &&
      (isCODPayment || isPaymentPaid));

  // Results & Reports tab: available once the order is completed, so the center
  // can upload the post-treatment report(s) for the patient.
  const showResultsTab = orderDetails?.status === "COMPLETED";
  // Uploaded report/result files (stored as TEST_RESULT booking documents).
  const testResults = (orderDetails?.documents || []).filter(
    (d) => String(d?.document_type || "").toUpperCase() === "TEST_RESULT",
  );
  // Once the report is shared with the patient, upload/delete/share are locked.
  const reportSent =
    String(orderDetails?.report_status || "").toUpperCase() === "SENT";

  // Service location = on-site drives the Start Processing vs Assign Therapist
  // button (and the tab is hidden for on-site). Based on the actual service
  // location, not the patient's preferred_location.
  const isOnSiteLocation = isOnSiteService;
  const therapistTabName = isOnSiteLocation ? "Processing" : "Therapist Detail";

  const tabs = [
    "Order Details",
    ...(showTherapistTab ? [therapistTabName] : []),
    ...(showResultsTab ? ["Results & Reports"] : []),
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-completed";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";
      case "Pending":
        return "status-cod";
      case "COD":
        return "status-cod";
      case "Payment Complete":
        return "status-payment";
      case "Collected":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Home Visit":
        return "status-labtype";
      case "Imaging":
        return "status-imagingtype";
      case "On-Site":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      case "Technician Dispatched":
        return "status-dispatched";
      case "Sent":
        return "status-dispatched";
      default:
        return "";
    }
  };

  const EditIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M11.9525 3.04199H13.25C13.4157 3.04199 13.5747 3.10784 13.6919 3.22505C13.8091 3.34226 13.875 3.50123 13.875 3.66699C13.875 3.83275 13.8091 3.99172 13.6919 4.10893C13.5747 4.22614 13.4157 4.29199 13.25 4.29199H12C10.0183 4.29199 8.59496 4.29366 7.51163 4.43866C6.44579 4.58199 5.80329 4.85533 5.32829 5.32949C4.85413 5.80449 4.58163 6.44616 4.43829 7.51199C4.29329 8.59532 4.29163 10.0187 4.29163 12.0003C4.29163 13.982 4.29329 15.4053 4.43829 16.4887C4.58163 17.5545 4.85496 18.197 5.32913 18.672C5.80413 19.1462 6.44579 19.4187 7.51163 19.562C8.59496 19.707 10.0183 19.7087 12 19.7087C13.9816 19.7087 15.405 19.707 16.4883 19.562C17.5541 19.4187 18.1966 19.1453 18.6716 18.6712C19.1458 18.1962 19.4183 17.5545 19.5616 16.4887C19.7066 15.4053 19.7083 13.982 19.7083 12.0003V10.7503C19.7083 10.5846 19.7741 10.4256 19.8913 10.3084C20.0086 10.1912 20.1675 10.1253 20.3333 10.1253C20.4991 10.1253 20.658 10.1912 20.7752 10.3084C20.8924 10.4256 20.9583 10.5846 20.9583 10.7503V12.0478C20.9583 13.972 20.9583 15.4795 20.8 16.6562C20.6383 17.8595 20.3 18.8095 19.555 19.5553C18.8091 20.3012 17.8591 20.6387 16.655 20.8003C15.4791 20.9587 13.9716 20.9587 12.0475 20.9587H11.9525C10.0283 20.9587 8.52079 20.9587 7.34413 20.8003C6.14079 20.6387 5.19079 20.3003 4.44496 19.5553C3.69913 18.8095 3.36163 17.8595 3.19996 16.6553C3.04163 15.4795 3.04163 13.972 3.04163 12.0478V11.9528C3.04163 10.0287 3.04163 8.52116 3.19996 7.34449C3.36163 6.14116 3.69996 5.19116 4.44496 4.44533C5.19079 3.69949 6.14079 3.36199 7.34496 3.20033C8.52079 3.04199 10.0283 3.04199 11.9525 3.04199ZM15.975 3.89699C16.5249 3.3622 17.2633 3.06536 18.0303 3.07068C18.7974 3.076 19.5316 3.38304 20.074 3.92541C20.6165 4.46777 20.9237 5.20187 20.9292 5.96895C20.9347 6.73603 20.638 7.47444 20.1033 8.02449L14.5633 13.5653C14.2541 13.8737 14.06 14.0687 13.8441 14.237C13.5894 14.436 13.3136 14.6065 13.0216 14.7453C12.7741 14.8628 12.5133 14.9495 12.0991 15.0878L9.67829 15.8945C9.45927 15.9674 9.22427 15.978 8.99961 15.9249C8.77494 15.8718 8.56949 15.7573 8.40626 15.594C8.24302 15.4308 8.12846 15.2253 8.07539 15.0007C8.02232 14.776 8.03285 14.541 8.10579 14.322L8.91246 11.9012C9.05079 11.4862 9.13746 11.2262 9.25496 10.9787C9.39496 10.6864 9.5644 10.4123 9.76329 10.1562C9.93163 9.93949 10.1258 9.74616 10.435 9.43699L15.975 3.89699ZM19.2191 4.78033C18.9061 4.46747 18.4817 4.29172 18.0391 4.29172C17.5966 4.29172 17.1721 4.46747 16.8591 4.78033L16.5458 5.09449C16.5636 5.17505 16.5908 5.26894 16.6275 5.37616C16.7466 5.72033 16.9733 6.17366 17.4 6.60033C17.8154 7.01596 18.3359 7.31115 18.9058 7.45449L19.2191 7.14116C19.532 6.82815 19.7077 6.40371 19.7077 5.96116C19.7077 5.5186 19.532 5.09333 19.2191 4.78033ZM17.9208 8.44033C17.3961 8.21408 16.9192 7.88998 16.5158 7.48533C16.1111 7.08187 15.787 6.60504 15.5608 6.08033L11.3483 10.292C11.0008 10.6395 10.865 10.777 10.7483 10.9253C10.6066 11.1093 10.4846 11.3076 10.3841 11.517C10.3025 11.6862 10.2408 11.8695 10.085 12.3353L9.72579 13.4145L10.5858 14.2753L11.665 13.9153C12.1316 13.7595 12.3141 13.6978 12.4841 13.617C12.6941 13.517 12.8913 13.395 13.0758 13.2512C13.2233 13.1353 13.3608 12.9995 13.7083 12.6528L17.9208 8.44033Z"
        fill="#053345"
      />
    </svg>
  );

  const testData = (orderDetails?.services || []).map((s, idx) => ({
    id: s.id || idx + 1,
    testName: s.service_name || "—",
    location: s.location === "HOME_VISIT" ? "Home Visit" : "On-Site",
    price: formatWholeAmount(s.price),
    numberOfSessions: s.number_of_sessions || 1,
  }));

  const testDataWithSessions = (orderDetails?.services || []).map((s, idx) => ({
    id: s.id || idx + 1,
    testName: s.service_name || "—",
    location: s.location === "HOME_VISIT" ? "Home Visit" : "On-Site",
    price: formatWholeAmount(s.price),
    numberOfSessions: s.number_of_sessions || 1,
    sessionsCompleted: s.sessions_completed || 0,
    sessionsRemaining: Math.max(0, (s.number_of_sessions || 1) - (s.sessions_completed || 0)),
  }));

  // ── Printable invoice ──────────────────────────────────────────────────────
  // NOTE: the invoice figure/label block, its status formatter and its home-visit flag all
  // lived here. The shared PhysioInvoiceModal now owns the invoice view-model (and its
  // bilingual labels), so there is no second copy to keep in step — which is exactly why the
  // loyalty discount row could previously appear in one place and not the other.

  const patientFullName = orderDetails?.patient
    ? [orderDetails.patient.full_name, orderDetails.patient.surname].filter(Boolean).join(" ")
    : "";

  // Child/dependent booking info (present only when a parent/guardian booked for
  // their child). Purely additive — the block below renders only for child bookings.
  // Supports both snake_case (is_for_child) and camelCase (isForChild) API shapes.
  const isChildBooking =
    orderDetails?.is_for_child === true || orderDetails?.isForChild === true;
  // Given name + surname, so the child reads the same way an adult patient does (this used to
  // drop the surname even though the booking snapshot carries it).
  const childName = composeBookingChildName(orderDetails) || null;
  // Whole years derived from the child's date of birth ('YYYY-MM-DD'), null-safe.
  const childAge = (() => {
    const dob = orderDetails?.child_date_of_birth ?? orderDetails?.childDateOfBirth;
    if (!dob) return null;
    const parts = String(dob).split("-");
    const birth =
      parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
        : new Date(dob);
    if (isNaN(birth.getTime())) return null;
    const now = new Date();
    let years = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) years--;
    return years >= 0 ? years : null;
  })();
  const childGender = orderDetails?.child_gender ?? orderDetails?.childGender;
  const childGenderLabel = childGender
    ? childGender.charAt(0).toUpperCase() + childGender.slice(1)
    : "—";
  // The CHILD is the person being treated on a child booking, so the header names the child
  // (child icon + name + "Child" badge), "Age | Gender" describes the child, and the account
  // holder is shown as the Guardian — the same split the doctor app's appointment details
  // make. Only when the snapshot has a name; otherwise the header keeps the account holder.
  const showChildAsPatient = isChildBooking && !!childName;

  // NOTE: the inline invoice + its `handlePrint` lived here. Printing is now owned by the
  // shared PhysioInvoiceModal, which renders AND prints the invoice itself.

  return (
    <PhysioDashboardLayout
      currentPage="physio_ordermanagement"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <div className={styles["page-header"]} style={{ cursor: 'pointer' }} onClick={() => navigate('/physio_ordermanagement')}>
        <FaArrowLeftLong />
        <h2>Appointment Detail</h2>
      </div>
      {orderLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: '4px solid #099494', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading order details...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      ) : (
      <div className={styles["apt-appointments-section"]}>
        <div className={`${styles["apt-section-header"]} mb-2`}>
          <h2>
            Appointment ID: <b>{orderDetails?.booking_number || "PT-00001"}</b>{" "}
            <span className={styles["tag-received"]} style={orderDetails?.status === 'COMPLETED' ? { color: '#065f46', backgroundColor: '#d1fae5' } : {}}>{orderDetails?.status ? orderDetails.status.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : "Received"}</span>{" "}
            {orderDetails?.is_urgent && <span className={styles["tag-urgent"]}>Urgent</span>}
          </h2>
          <div className={styles["apt-top-actions"]}>
            {/* Center-action SLA — visible only while the order is awaiting the
                center's first action (REQUEST_SUBMITTED). Matches the lab detail
                timer design. */}
            <div style={{ display: "inline-flex", alignItems: "center" }}>
              <SlaTimer
                variant="detail"
                dueAt={orderDetails?.sla_due_at}
                isUrgent={orderDetails?.sla_is_urgent}
                status={orderDetails?.status}
                visibleStatuses={["REQUEST_SUBMITTED"]}
              />
            </div>
            {/* Print Invoice — same design as the nurse app (wallet-withdraw-btn).
                Hidden while the order is still in ASSESSMENT (no invoice yet). */}
            {invoiceSent && orderDetails?.status !== 'ASSESSMENT' && (
              <button
                type="button"
                className={styles["wallet-withdraw-btn"]}
                onClick={() => setShowInvoiceModal(true)}
              >
                <FiPrinter size={16} /> Print Invoice
              </button>
            )}

            {/* Chat icon — shown on all tabs (matches the lab app); opens the
                same shared Chat modal. */}
            <>
                <button
                  className={styles["chat-icon-btn"]}
                  onClick={toggleChat}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0C4.477 0 0 4.477 0 10C0 11.6 0.376 13.112 1.043 14.453C1.221 14.809 1.28 15.216 1.177 15.601L0.582 17.827C0.52307 18.0473 0.523124 18.2792 0.582155 18.4995C0.641186 18.7197 0.757118 18.9206 0.918315 19.0819C1.07951 19.2432 1.2803 19.3593 1.50053 19.4184C1.72075 19.4776 1.95267 19.4778 2.173 19.419L4.399 18.823C4.78538 18.7254 5.19414 18.7727 5.548 18.956C6.93095 19.6446 8.45512 20.002 10 20ZM6 11.25C5.80109 11.25 5.61032 11.329 5.46967 11.4697C5.32902 11.6103 5.25 11.8011 5.25 12C5.25 12.1989 5.32902 12.3897 5.46967 12.5303C5.61032 12.671 5.80109 12.75 6 12.75H11.5C11.6989 12.75 11.8897 12.671 12.0303 12.5303C12.171 12.3897 12.25 12.1989 12.25 12C12.25 11.8011 12.171 11.6103 12.0303 11.4697C11.8897 11.329 11.6989 11.25 11.5 11.25H6ZM5.25 8.5C5.25 8.30109 5.32902 8.11032 5.46967 7.96967C5.61032 7.82902 5.80109 7.75 6 7.75H14C14.1989 7.75 14.3897 7.82902 14.5303 7.96967C14.671 8.11032 14.75 8.30109 14.75 8.5C14.75 8.69891 14.671 8.88968 14.5303 9.03033C14.3897 9.17098 14.1989 9.25 14 9.25H6C5.80109 9.25 5.61032 9.17098 5.46967 9.03033C5.32902 8.88968 5.25 8.69891 5.25 8.5Z"
                      fill="#2B7FFF"
                    />
                  </svg>
                </button>

                {isChatOpen && (
                  <Chat
                    patientUserId={orderDetails?.patient_id}
                    bookingId={orderDetails?.booking_id || orderId}
                    title={patientFullName || "Patient"}
                    subtitle={orderDetails?.booking_number || ""}
                    onClose={toggleChat}
                  />
                )}
              </>
          </div>
        </div>

        <div className={styles["patient-appointment-tabs"]}>
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`${styles["patient-tab"]} ${
                activeTab === tab ? styles["active"] : ""
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "Order Details" && (
          <>
            <div className={styles["patient-tab-content"]}>
              {/* Acceptance required: a newly received order must be accepted
                  (or rejected) before the center can add therapies, set the
                  service date/time or send the invoice. Shown first so the
                  decision is the first thing the officer sees. */}
              {isAwaitingCenterDecision && (
                <section
                  className={styles["acceptance-panel"]}
                  aria-labelledby="acceptance-panel-title"
                >
                  <div className={styles["acceptance-panel-body"]}>
                    <span
                      className={styles["acceptance-panel-icon"]}
                      aria-hidden="true"
                    >
                      <FaInfoCircle size={16} />
                    </span>
                    <div>
                      <h3
                        id="acceptance-panel-title"
                        className={styles["acceptance-panel-title"]}
                      >
                        {t(
                          "physioAppointmentDetail.acceptanceRequiredTitle",
                          "Acceptance required",
                        )}
                      </h3>
                      <p className={styles["acceptance-panel-text"]}>
                        {t(
                          "physioAppointmentDetail.acceptanceRequiredMessage",
                          "This is a new order request. Review the order details below, then accept or reject it. You can't add therapies, set the service date and time, or send the invoice until the order is accepted.",
                        )}
                      </p>
                      <ul className={styles["acceptance-panel-list"]}>
                        <li>
                          <strong>
                            {t("physioAppointmentDetail.accept", "Accept")}:
                          </strong>{" "}
                          {t(
                            "physioAppointmentDetail.acceptanceAcceptHelp",
                            "your center takes this order. The patient is notified and you can continue with therapies and the invoice.",
                          )}
                        </li>
                        <li>
                          <strong>
                            {t("physioAppointmentDetail.reject", "Reject")}:
                          </strong>{" "}
                          {t(
                            "physioAppointmentDetail.acceptanceRejectHelp",
                            "the order is declined and moved to the Rejected tab. The patient is notified.",
                          )}
                        </li>
                      </ul>
                    </div>
                  </div>
                  {/* The dialog button classes are nested under .labDialog_wrapper
                      in the SCSS, so the actions container carries that class. */}
                  <div
                    className={`${dialogStyles["labDialog_wrapper"]} ${styles["acceptance-panel-actions"]}`}
                  >
                    <button
                      type="button"
                      className={`${dialogStyles["btn-1"]} ${styles["acceptance-panel-btn"]}`}
                      onClick={() => setShowAcceptModal(true)}
                      // Same height as the outlined Reject button.
                      style={{ border: "1px solid transparent" }}
                    >
                      <FaCheck aria-hidden="true" />{" "}
                      {t("physioAppointmentDetail.acceptOrder", "Accept Order")}
                    </button>
                    <button
                      type="button"
                      className={`${dialogStyles["btn-outlined-1"]} ${styles["acceptance-panel-btn"]}`}
                      onClick={() => {
                        setRejectReason("");
                        setShowRejectModal(true);
                      }}
                    >
                      {t("physioAppointmentDetail.rejectOrder", "Reject Order")}
                    </button>
                  </div>
                </section>
              )}

              {/* Order rejected: the red counterpart of the "Acceptance
                  required" panel, in the same place. States that the order was
                  rejected and shows the reason the officer gave. */}
              {isOrderRejected && (
                <section
                  className={styles["rejection-panel"]}
                  aria-labelledby="rejection-panel-title"
                >
                  <span
                    className={styles["rejection-panel-icon"]}
                    aria-hidden="true"
                  >
                    <IoClose size={18} />
                  </span>
                  <div className={styles["rejection-panel-body"]}>
                    <h3
                      id="rejection-panel-title"
                      className={styles["rejection-panel-title"]}
                    >
                      {t("physioAppointmentDetail.rejectedTitle", "Order rejected")}
                    </h3>
                    <p className={styles["rejection-panel-text"]}>
                      {t(
                        "physioAppointmentDetail.rejectedMessage",
                        "This order was rejected and the patient has been notified.",
                      )}
                    </p>
                    <div className={styles["rejection-panel-reason"]}>
                      <span className={styles["rejection-panel-reason-label"]}>
                        {t(
                          "physioAppointmentDetail.rejectionReasonLabel",
                          "Reason for rejection",
                        )}
                      </span>
                      {orderRejectionReason ? (
                        <p className={styles["rejection-panel-reason-text"]}>
                          {orderRejectionReason}
                        </p>
                      ) : (
                        <p
                          className={`${styles["rejection-panel-reason-text"]} ${styles["rejection-panel-reason-empty"]}`}
                        >
                          {t(
                            "physioAppointmentDetail.noRejectionReason",
                            "No reason was given.",
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* Invoice sent: the blue counterpart of the panels above, in the
                  same place. Shown while the invoice (or a revisit's next-session
                  request) is with the patient and the payment is still pending. */}
              {isAwaitingPatientPayment && (
                <section
                  className={styles["invoice-sent-panel"]}
                  aria-labelledby="invoice-sent-panel-title"
                >
                  <span
                    className={styles["invoice-sent-panel-icon"]}
                    aria-hidden="true"
                  >
                    <FaFileInvoice size={15} />
                  </span>
                  <div className={styles["invoice-sent-panel-body"]}>
                    <h3
                      id="invoice-sent-panel-title"
                      className={styles["invoice-sent-panel-title"]}
                    >
                      {t("physioAppointmentDetail.invoiceSentTitle", "Invoice sent")}
                    </h3>
                    <p className={styles["invoice-sent-panel-text"]}>
                      {isRevisitAwaitingPayment
                        ? t(
                            "physioAppointmentDetail.revisitInvoiceSentMessage",
                            "The revisit request has been sent to the patient. Once the patient completes the payment, the order will proceed.",
                          )
                        : t(
                            "physioAppointmentDetail.invoiceSentMessage",
                            "The invoice has been sent to the patient. Waiting for the patient to complete the payment.",
                          )}
                    </p>
                  </div>
                </section>
              )}

              <div
                className={`${styles["white-card"]} ${styles["patient-info-card"]}`}
              >
                <div
                  className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}
                >
                  <h2>Patient Information</h2>
                  <div className={styles["patient-actions-wrap"]}></div>
                </div>

                {/* Out-of-operating-hours banner: shown when the patient's
                    preferred date/slot falls outside the center's operating
                    hours (closed day, period out of hours, or already passed). */}
                {(() => {
                  // Once the schedule is fixed (invoice sent or order rejected), a
                  // later change to the operating hours no longer raises this banner.
                  if (isServiceScheduleFixed) return null;
                  const banner = buildSlotBanner();
                  if (!banner || banner.key === dismissedBannerKey) return null;
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        background: "#FEF2F2",
                        border: "1px solid #FECACA",
                        borderRadius: "10px",
                        padding: "12px 14px",
                        margin: "0 0 16px",
                      }}
                    >
                      <span
                        style={{
                          flexShrink: 0,
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          background: "#FEE2E2",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#DC2626",
                        }}
                      >
                        <FaClock style={{ fontSize: "13px" }} />
                      </span>
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1 }}>
                        <span style={{ color: "#B91C1C", fontSize: "13px", fontWeight: 700 }}>
                          {banner.title}
                        </span>
                        <span style={{ color: "#7F1D1D", fontSize: "12.5px", lineHeight: 1.45 }}>
                          {banner.detail}
                        </span>
                      </div>
                      <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={() => setDismissedBannerKey(banner.key)}
                        style={{
                          flexShrink: 0,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#B91C1C",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "2px",
                          lineHeight: 1,
                        }}
                      >
                        <IoClose style={{ fontSize: "16px" }} />
                      </button>
                    </div>
                  );
                })()}

                <div className={styles["field-block"]}>
                  <div className={`${styles["label"]} mb-2`}>Patient Name</div>
                  <div className={styles["user-section"]}>
                    <div className={styles["userInfo"]}>
                      {/* No patient photo or child icon; the heading and "Child" badge say
                          who the patient is (same as the doctor app). */}
                      <h3>
                        {showChildAsPatient
                          ? childName
                          : orderDetails?.patient ? [orderDetails.patient.full_name, orderDetails.patient.surname].filter(Boolean).join(" ") : "—"}
                        {showChildAsPatient && (
                          <span
                            // Same teal pill the lab app's order details use.
                            style={{
                              marginLeft: "8px",
                              fontSize: "11px",
                              background: "#0cbdbd",
                              color: "#fff",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontWeight: 500,
                              verticalAlign: "middle",
                            }}
                          >
                            Child
                          </span>
                        )}
                      </h3>
                    </div>

                    {/* <div className={styles["msgIcon"]}>
                      <BsFillChatTextFill />
                    </div> */}
                  </div>
                </div>

                {/* Child/dependent booking — rendered only when a parent/guardian
                    booked for their child (is_for_child). Otherwise renders nothing. */}
                {isChildBooking && (
                  <div className={styles["field-block"]}>
                    <div className={`${styles["label"]} mb-2`}>Service For: Child</div>
                    <div className={styles["medical-history"]}>
                      <div className={`${styles["history-grid"]} ${styles["med-grid"]}`}>
                        <div className={styles["field-block"]}>
                          <div className={styles["label"]}>Child Name</div>
                          <div className={styles["value"]}>{childName || "—"}</div>
                        </div>

                        <div className={styles["field-block"]}>
                          <div className={styles["label"]}>Age</div>
                          <div className={styles["value"]}>{childAge != null ? `${childAge} yr` : "—"}</div>
                        </div>

                        <div className={styles["field-block"]}>
                          <div className={styles["label"]}>Gender</div>
                          <div className={styles["value"]}>{childGenderLabel}</div>
                        </div>

                        {/* The account holder who booked for the child. */}
                        <div className={styles["field-block"]}>
                          <div className={styles["label"]}>Guardian Name</div>
                          <div className={styles["value"]}>{patientFullName || "—"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className={styles["medical-history"]}>
                  <div
                    className={`${styles["history-grid"]} ${styles["med-grid"]}`}
                  >
                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Age | Gender</div>
                      {/* Same person as the header: the child on a child booking. */}
                      <div className={styles["value"]}>{showChildAsPatient
                        ? `${childAge != null ? `${childAge} yr` : "—"} | ${childGenderLabel}`
                        : <>{orderDetails?.patient?.age ? `${orderDetails.patient.age} yr` : "—"} | {orderDetails?.patient?.gender ? orderDetails.patient.gender.charAt(0).toUpperCase() + orderDetails.patient.gender.slice(1) : "—"}</>}</div>
                    </div>

                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Mobile Number</div>
                      <div className={styles["value"]}>{orderDetails?.patient?.mobile_number || "—"}</div>
                    </div>

                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Preferred Date</div>
                      <div className={styles["value"]}>{orderDetails?.preferred_date ? (() => {
                        const parts = orderDetails.preferred_date.split('-');
                        if (parts.length === 3) {
                          const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                          return tzLocale(d, 'en-GB', undefined);
                        }
                        return tzDateNumeric(new Date(orderDetails.preferred_date));
                      })() : "—"}</div>
                    </div>

                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Preferred Time Slot</div>
                      <div className={styles["value"]}>{orderDetails?.preferred_time_slot ? (() => {
                        const slot = orderDetails.preferred_time_slot.toUpperCase();
                        const periodName = slot === 'MORNING' ? 'Morning' : slot === 'AFTERNOON' ? 'Afternoon' : slot === 'EVENING' ? 'Evening' : `${orderDetails.preferred_time_slot.charAt(0).toUpperCase() + orderDetails.preferred_time_slot.slice(1).toLowerCase()}`;
                        // When the preferred period is outside the center's operating
                        // hours for that date (closed day / period out of hours / already
                        // passed today), show the period with a clear "Outside hours" tag
                        // instead of a misleading time range.
                        // Not flagged once the schedule is fixed (invoice sent or order
                        // rejected): a later operating-hours change doesn't matter.
                        const status = evaluatePreferredSlotStatus();
                        if (!isServiceScheduleFixed && status.loaded && !status.open) {
                          // Reason-specific tag: a fully closed day reads "Center
                          // closed" (not "outside hours"), an elapsed today slot
                          // reads "Already passed", else "Outside operating hours".
                          const tagText =
                            status.reason === "CLOSED_DAY" ? "Center closed"
                              : status.reason === "PAST" ? "Already passed"
                              : "Outside operating hours";
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                              <span>{periodName}</span>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: "#FEF2F2",
                                  color: "#B91C1C",
                                  border: "1px solid #FECACA",
                                  borderRadius: "999px",
                                  padding: "1px 8px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {tagText}
                              </span>
                            </span>
                          );
                        }
                        // Otherwise: the operating-hours-clipped window when open, else
                        // the generic period band as a fallback until slots load.
                        const win = getPreferredSlotWindow();
                        const staticRange = slot === 'MORNING' ? '8:00 AM - 12:00 PM' : slot === 'AFTERNOON' ? '12:00 PM - 5:00 PM' : slot === 'EVENING' ? '5:00 PM - 7:30 PM' : null;
                        const rangeText = win ? `${minutesToLabel12(win.startMin)} - ${minutesToLabel12(win.endMin)}` : staticRange;
                        return rangeText ? `${periodName} (${rangeText})` : periodName;
                      })() : "—"}</div>
                    </div>

                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Preferred Location</div>
                      <div className={styles["value"]}>
                        <span
                          className={`${styles["db-status"]} ${
                            styles[getStatusColor(orderDetails?.preferred_location === "HOME_VISIT" ? "Home Visit" : "On-Site")]
                          }`}
                        >
                          {orderDetails?.preferred_location === "HOME_VISIT" ? "Home Visit" : "On-Site"}
                        </span>
                      </div>
                    </div>

                    {/* Service Date & Time: always shown for physio officer to fill */}
                    <div className={styles["field-block"]} id="service-date-field">
                      <div className={styles["label"]}>Service Date {!serviceDate && <span style={{ color: '#EF4444', fontSize: '12px' }}>*</span>}</div>
                      <div className={styles["date-wrapper"]} style={validationErrors.serviceDate ? { border: '1.5px solid #EF4444', borderRadius: '8px' } : {}}>
                        <DatePicker
                          className={styles["apt-date-filter"]}
                          selected={serviceDate}
                          onChange={(d) => { dateSelectedByUserRef.current = true; setServiceDate(d); setServiceTimeTipDismissed(false); setValidationErrors(prev => ({ ...prev, serviceDate: false })); }}
                          placeholderText="Select service date"
                          dateFormat="dd/MM/yyyy"
                          minDate={minServiceDate}
                          disabled={scheduleInputsLocked || (!!orderDetails?.scheduled_date && orderDetails?.status !== 'ASSESSMENT')}
                        />
                      </div>
                      {validationErrors.serviceDate && <div style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>Service date is required</div>}
                    </div>

                    <div className={styles["field-block"]} id="service-time-field">
                      <div className={styles["label"]}>Service Time {!serviceTime && <span style={{ color: '#EF4444', fontSize: '12px' }}>*</span>}</div>
                      <div className={styles["value"]}>
                        <div className={styles["apt-field-wrapper"]} style={validationErrors.serviceTime ? { border: '1.5px solid #EF4444', borderRadius: '8px' } : {}}>
                          <RxClock className={styles["apt-field-icon"]} />
                          <select
                            value={serviceTime}
                            onChange={(e) => { setServiceTime(e.target.value); setValidationErrors(prev => ({ ...prev, serviceTime: false })); }}
                            className={styles["apt-field-input"]}
                            style={{ appearance: 'none', paddingRight: '30px', cursor: (scheduleInputsLocked || (orderDetails?.scheduled_time && orderDetails?.status !== 'ASSESSMENT')) ? 'not-allowed' : 'pointer', opacity: (scheduleInputsLocked || (orderDetails?.scheduled_time && orderDetails?.status !== 'ASSESSMENT')) ? 0.6 : 1 }}
                            disabled={scheduleInputsLocked || (!!orderDetails?.scheduled_time && orderDetails?.status !== 'ASSESSMENT')}
                          >
                            <option value="">Select time</option>
                            {timeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <RxChevronDown style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9CA3AF' }} />
                        </div>
                      </div>
                      {validationErrors.serviceTime && <div style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>Service time is required</div>}
                      {/* Center is open but the preferred period has no bookable slots
                          for this date (outside operating hours, or all slots already
                          passed today) — shown as a dismissible tooltip on an amber chip. */}
                      {(() => {
                        // Not shown once the schedule is fixed (invoice sent or order rejected).
                        if (isServiceScheduleFixed) return null;
                        if (!serviceDate || !orderDetails?.preferred_time_slot) return null;
                        const selStr = formatDateYMD(serviceDate);
                        if (!centerSlots || centerSlots.date !== selStr) return null;
                        if (centerSlots.is_open === false) return null; // handled by the closed badge/toast
                        if (timeOptions.length > 0) return null;
                        if (serviceTimeTipDismissed) return null;
                        const periodName = orderDetails.preferred_time_slot.charAt(0).toUpperCase() + orderDetails.preferred_time_slot.slice(1).toLowerCase();
                        return (
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "flex-start",
                              marginTop: "6px",
                              background: "#FFFBEB",
                              border: "1px solid #FDE68A",
                              borderRadius: "8px",
                              padding: "8px 10px",
                            }}
                          >
                            <FaInfoCircle style={{ color: "#D97706", marginTop: "2px", flexShrink: 0 }} />
                            <span style={{ color: "#92400E", fontSize: "12px", lineHeight: 1.45, flex: 1 }}>
                              No available time slots for the {periodName} slot on this date. Please choose another date.
                            </span>
                            <button
                              type="button"
                              aria-label="Close"
                              onClick={() => setServiceTimeTipDismissed(true)}
                              style={{
                                flexShrink: 0,
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#B45309",
                                display: "inline-flex",
                                padding: 0,
                              }}
                            >
                              <IoClose style={{ fontSize: "15px" }} />
                            </button>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Center Availability — reflects the SELECTED service date (updates on change) */}
                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Center Availability</div>
                      <div className={styles["value"]}>
                        {(() => {
                          // Same badge shape as Payment Status, with the center-availability colors.
                          const badgeBase = { padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500 };
                          if (!serviceDate) return <span style={{ color: "#6B7280", fontSize: "13px" }}>Select a service date</span>;
                          // Only show availability that matches the currently selected
                          // service date (avoids a stale badge while the fetch is in flight).
                          const selStr = formatDateYMD(serviceDate);
                          if (!centerSlots || centerSlots.date !== selStr) {
                            return <span style={{ color: "#6B7280", fontSize: "13px" }}>Checking availability…</span>;
                          }
                          if (centerSlots.is_open === false) {
                            // Schedule fixed (invoice sent or order rejected): a later
                            // change to the operating hours is not flagged.
                            if (isServiceScheduleFixed) return <span style={{ color: "#6B7280", fontSize: "13px" }}>—</span>;
                            return <span style={{ ...badgeBase, backgroundColor: "#FEE2E2", color: "#B91C1C" }}>Center closed</span>;
                          }
                          if (centerSlots.is_open === true) {
                            return <span style={{ ...badgeBase, backgroundColor: "#DCFCE7", color: "#15803D" }}>Center open</span>;
                          }
                          return <span style={{ color: "#6B7280", fontSize: "13px" }}>—</span>;
                        })()}
                      </div>
                    </div>

                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Payment Method</div>
                      <div className={styles["value"]}>
                        {(() => {
                          const method = orderDetails?.payment_method;
                          if (!method) return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#F3F4F6', color: '#6B7280' }}>-</span>;
                          if (method.toUpperCase() === 'COD') return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#FEF3C7', color: '#92400E' }}>COD</span>;
                          if (method.toUpperCase() === 'WALLET') return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#DBEAFE', color: '#1E40AF' }}>Wallet</span>;
                          if (method.toUpperCase() === 'CARD') return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#E0E7FF', color: '#3730A3' }}>Card</span>;
                          return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#F3E8FF', color: '#7C3AED' }}>{method}</span>;
                        })()}
                      </div>
                    </div>

                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Payment Status</div>
                      <div className={styles["value"]}>
                        {(() => {
                          const status = orderDetails?.payment_status;
                          if (!status || status === 'PENDING') return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#FEF3C7', color: '#92400E' }}>Pending</span>;
                          if (status === 'PAID' || status === 'COMPLETED') return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#D1FAE5', color: '#065F46' }}>Paid</span>;
                          if (status === 'FAILED') return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Failed</span>;
                          return <span style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, backgroundColor: '#F3F4F6', color: '#374151' }}>{status}</span>;
                        })()}
                      </div>
                    </div>

                    <div className={styles["field-block"]}>
                      <div className={styles["label"]}>Service Location</div>
                      <div className={styles["value"]}>
                        {(() => {
                          const services = orderDetails?.services || [];
                          if (services.length === 0) {
                            // No services yet, show preferred location
                            return (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(orderDetails?.preferred_location === "HOME_VISIT" ? "Home Visit" : "On-Site")]
                                }`}
                              >
                                {orderDetails?.preferred_location === "HOME_VISIT" ? "Home Visit" : "On-Site"}
                              </span>
                            );
                          }
                          // The whole order shares a single location (no split orders).
                          const hasHomeVisit = services.some(s => normalizeLocationCode(s.location) === LOCATION_CODES.HOME_VISIT);
                          const displayLocation = hasHomeVisit ? "Home Visit" : "On-Site";
                          return (
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(displayLocation)]
                              }`}
                            >
                              {displayLocation}
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* case note and patient address in 2 columns  */}
              <div className={`row mb-3 pb-1 ${styles["details-grid"]} `}>
                <div className={`col-lg-6 ${styles["details-grid-leftCol"]}`}>
                  <div className={styles["white-card"]}>
                    <div className={styles["patient-tab-title"]}>
                      <h2>Case Note</h2>
                    </div>

                    <div className={styles["grid-col-4"]}>
                      <div className={styles["field-block"]}>
                        <div
                          className={
                            styles["value"] +
                            " " +
                            styles["font-medium"] +
                            " " +
                            styles["address-box"]
                          }
                        >
                          {orderDetails?.case_notes || "No case notes available"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`${styles["white-card"]} ${styles["address-card"]} mb-0`}
                  >
                    <div className={styles["patient-tab-title"]}>
                      <h2>Patient Address</h2>
                    </div>

                    <div className={styles["grid-col-4"]}>
                      <div
                        className={`${styles["field-block"]} ${styles["address-field"]}`}
                      >
                        <div
                          className={
                            styles["value"] +
                            " " +
                            styles["font-medium"] +
                            " " +
                            styles["address-box"]
                          }
                        >
                          <LiaMapMarkerSolid /> {orderDetails?.patient_address ? `${orderDetails.patient_address.house_flat_floor_no || ''} ${orderDetails.patient_address.street || ''}, ${orderDetails.patient_address.area_district || ''}, ${orderDetails.patient_address.city || ''}`.replace(/^[\s,]+|[\s,]+$/g, '') : "No address available"}
                        </div>
                        {(() => {
                          const addr = orderDetails?.patient_address;
                          const hasCoords =
                            addr &&
                            addr.latitude != null && addr.latitude !== '' &&
                            addr.longitude != null && addr.longitude !== '' &&
                            !isNaN(Number(addr.latitude)) && !isNaN(Number(addr.longitude));
                          const textAddress = addr
                            ? [
                                addr.house_flat_floor_no,
                                addr.street,
                                addr.area_district,
                                addr.city,
                                addr.governorate,
                                addr.country,
                              ].filter(Boolean).join(', ')
                            : '';
                          const destination = hasCoords
                            ? `${addr.latitude},${addr.longitude}`
                            : textAddress;
                          const mapsUrl = destination
                            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving&dir_action=navigate`
                            : null;
                          return mapsUrl ? (
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={styles["maps-link"]}>Open in Maps</a>
                          ) : (
                            <span className={`${styles["maps-link"]} ${styles["maps-link-disabled"]}`}>Open in Maps</span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div
                    className={`${styles["white-card"]} ${styles["prescription-card"]}`}
                  >
                    <div className={styles["patient-tab-title"]}>
                      <h2>Prescription</h2>
                    </div>
                    <div>
                      <div className={styles["medical-history"]}>
                        {(() => {
                          const prescriptionDocs = orderDetails?.documents?.filter(d => d.document_type === 'PRESCRIPTION') || [];
                          if (prescriptionDocs.length > 0) {
                            return prescriptionDocs.map((doc, index) => {
                              const docUrl = doc.presigned_url || doc.presignedUrl;
                              // The backend verifies the S3 object exists; when it doesn't
                              // (never uploaded correctly / removed) file_available is false
                              // and we show a friendly "not uploaded properly" state instead
                              // of an S3 error in the iframe (same idea as the pharmacy app).
                              const fileAvailable = doc.file_available !== false && !!docUrl;
                              return (
                                <div key={doc.id || index} className={styles["upload-row"]}>
                                  <div className={styles["upload-item"] + " " + styles["w-100"]}>
                                    <div className={styles["file-name"]}>
                                      <AiOutlineFilePdf size={30} className="text-danger" />
                                      <span className="d-flex flex-column">
                                        <strong>{doc.file_name || `Prescription ${index + 1}`}</strong>
                                        <span>
                                          {doc.uploaded_at ? `Uploaded: ${tzCustom(new Date(doc.uploaded_at), { month: 'short', day: 'numeric', year: 'numeric' })} at ${tzCustom(new Date(doc.uploaded_at), { hour: 'numeric', minute: '2-digit', hour12: true })}` : ''}
                                        </span>
                                      </span>
                                    </div>
                                    {fileAvailable && (
                                      <a href={docUrl} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}>
                                        <FiDownload size={20} />
                                      </a>
                                    )}
                                  </div>
                                  {fileAvailable ? (
                                    <div className={styles["preview-scroller"]}>
                                      <div className={styles["uploaded-preview"]}>
                                        <div className={styles["preview-wrapper"]}>
                                          <iframe
                                            src={docUrl}
                                            style={{ width: '100%', height: '250px', border: 'none', borderRadius: '8px' }}
                                            title={`Prescription ${index + 1}`}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        border: '1px dashed #FCA5A5',
                                        borderRadius: '8px',
                                        backgroundColor: '#FEF2F2',
                                        padding: '32px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        gap: '6px',
                                        marginTop: '8px',
                                      }}
                                    >
                                      <AiOutlineFilePdf size={34} style={{ color: '#B91C1C', opacity: 0.6 }} />
                                      <div style={{ fontSize: 14, fontWeight: 600, color: '#B91C1C' }}>
                                        Prescription not uploaded properly
                                      </div>
                                      <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 360, lineHeight: 1.5 }}>
                                        We couldn't load this prescription. It may not have been uploaded correctly or the file is no longer available. Please ask the patient to re-upload it.
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          } else {
                            return (
                              <div className="text-center py-4">
                                <AiOutlineFilePdf size={48} className="text-muted mb-2" />
                                <p className="text-muted mb-0">No prescription found</p>
                                <small className="text-muted">No prescription has been uploaded for this order</small>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Medical Documents — same simple design as lab/imaging (a
                  clickable bordered row per document with a red PDF icon +
                  filename that opens the presigned URL in a new tab). Rows are
                  spaced with a vertical gap and the list has an outer margin so
                  it doesn't sit flush against the card border. */}
              <div className={styles["white-card"]}>
                <div className={styles["patient-tab-title"]}>
                  <h2>Medical Documents</h2>
                </div>
                <div>
                  <div
                    className={styles["medical-history"]}
                    style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "12px 4px" }}
                  >
                    {(() => {
                      const medicalDocs =
                        orderDetails?.documents?.filter(
                          (doc) => doc.document_type === "MEDICAL_DOCUMENT"
                        ) || [];

                      if (medicalDocs.length === 0) {
                        return (
                          <div className="p-3 text-secondary fw-semibold">
                            No medical document uploaded
                          </div>
                        );
                      }

                      return medicalDocs.map((doc, index) => {
                        const fileUrl =
                          doc.presigned_url || doc.presignedUrl || doc.file_url;
                        return (
                          <div
                            key={doc.id || index}
                            className={styles["upload-row"]}
                          >
                            <a
                              className={
                                styles["upload-item"] + " " + styles["w-100"]
                              }
                              href={fileUrl || "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: fileUrl ? "pointer" : "default",
                                textDecoration: "none",
                                color: "inherit",
                                width: "100%",
                              }}
                            >
                              <div className={styles["file-name"]}>
                                <AiOutlineFilePdf size={30} className="text-danger" />
                                <span>{doc.file_name || "Medical Document"}</span>
                              </div>
                            </a>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>


              {/* Assessment Completed Banner - show when assessment flow is done */}
              {orderDetails?.assessment_required && orderDetails?.sessions_updated_by_therapist && orderDetails?.status !== 'REQUEST_SUBMITTED' && (
                <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#f0fdfa', border: '1px solid #099494', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>✅</span>
                  <div>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#065f46' }}>Assessment Completed</p>
                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#047857' }}>Therapist has completed the assessment and updated the sessions for this order.</p>
                  </div>
                </div>
              )}

              {/* Assessment In Progress Banner - show when assessment is assigned but not yet completed */}
              {orderDetails?.assessment_required && !orderDetails?.sessions_updated_by_therapist && orderDetails?.assigned_therapist_id && orderDetails?.status !== 'REQUEST_SUBMITTED' && (
                <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>⏳</span>
                  <div>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#92400e' }}>Assessment In Progress</p>
                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#b45309' }}>Waiting for therapist to complete the assessment and update sessions.</p>
                  </div>
                </div>
              )}

              {/* Assessment Mode Toggle - show when order is REQUEST_SUBMITTED and accepted */}
              {orderDetails?.status === 'REQUEST_SUBMITTED' && isPhysioAccepted && !isPostSplitConfirmation && (
                <div className={styles["white-card"]} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>Assessment Required</h3>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                        {assessmentMode ? 'Therapist will be assigned for assessment first' : 'Invoice will be sent directly to patient'}
                      </p>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={assessmentMode}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          setAssessmentMode(newValue);
                          // Save to backend
                          try {
                            const token = sessionStorage.getItem("usr_1a2b3c");
                            const axiosInstance = axios.create({
                              baseURL: process.env.REACT_APP_API_URL,
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            await axiosInstance.put(`${APP_PATH.physioOrders}/${orderId}/status`, {
                              status: orderDetails?.status,
                              assessmentRequired: newValue,
                            });
                          } catch (err) {
                            console.error('Failed to update assessment flag:', err);
                          }
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: assessmentMode ? '#099494' : '#ccc',
                        borderRadius: '26px', transition: '0.3s',
                      }} />
                      <span style={{
                        position: 'absolute', top: '3px', left: assessmentMode ? '25px' : '3px',
                        width: '20px', height: '20px', backgroundColor: '#fff',
                        borderRadius: '50%', transition: '0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </label>
                  </div>
                </div>
              )}

              {showTherapyAndInvoiceSections && (
              <>
              {/* Therapy Details */}
              {orderDetails?.services && orderDetails.services.length > 0 ? (
              <div className={styles["white-card"]}>
                <div className={styles["patient-tab-title"]}>
                  <h2>Therapy Details</h2>
                  {((orderDetails?.status === 'REQUEST_SUBMITTED' && isPhysioAccepted) || (orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) && !isPostSplitConfirmation && (
                  <Link href="" className={styles["add-test-link"]} onClick={(e) => { e.preventDefault(); setShowAddTherapyRow(true); }}>
                    <FaCirclePlus /> Add Therapy
                  </Link>
                  )}
                </div>
                <div className={styles["bg-gray-light"]}>
                  <div className={`${styles["apt-appointments-table"]} ${styles["test-details-table"]}`}>
                    <table>
                      <thead>
                        <tr>
                          <th>Therapy Name</th>
                          <th>Number of Sessions</th>
                          <th>Sessions Completed</th>
                          <th>Sessions Remaining</th>
                          <th>Location</th>
                          <th>Price</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testDataWithSessions.map((item, idx) => (
                          <tr key={idx}>
                            {editingServiceId === (orderDetails.services[idx]?.id) ? (
                              <>
                                <td>{item.testName}</td>
                                <td>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={editServiceData.number_of_sessions !== undefined ? editServiceData.number_of_sessions : ""}
                                    onChange={(e) => setEditServiceData({...editServiceData, number_of_sessions: e.target.value.replace(/[^0-9]/g, "")})}
                                    className={styles["apt-field-input"]}
                                    style={{width:'100px', padding:'6px 10px'}}
                                  />
                                </td>
                                <td>{item.sessionsCompleted}</td>
                                <td>{(Number(editServiceData.number_of_sessions) || item.numberOfSessions) - item.sessionsCompleted}</td>
                                <td>
                                  <select value={editServiceData.location || "ON_SITE"} onChange={(e) => setEditServiceData({...editServiceData, location: e.target.value})} className={styles["apt-status-filter"]}>
                                    {allowedLocations.includes(LOCATION_CODES.ON_SITE) && <option value="ON_SITE">On-Site</option>}
                                    {allowedLocations.includes(LOCATION_CODES.HOME_VISIT) && <option value="HOME_VISIT">Home Visit</option>}
                                  </select>
                                </td>
                                <td>
                                  <AmountInput
                                    value={editServiceData.price !== undefined ? editServiceData.price : ""}
                                    onValueChange={(value) => setEditServiceData({...editServiceData, price: value})}
                                    className={styles["apt-field-input"]}
                                    style={{width:'100px', padding:'6px 10px'}}
                                  />
                                </td>
                                <td>
                                  <div className={styles["apt-action-buttons"]}>
                                    <button className={styles["apt-view-btn"]} title="Save" onClick={() => handleSaveEdit(orderDetails.services[idx]?.id)}>
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15.7489 10.1671C15.8174 10.1032 15.8724 10.0262 15.9105 9.94061C15.9487 9.85503 15.9692 9.76264 15.9708 9.66897C15.9725 9.57529 15.9553 9.48224 15.9202 9.39537C15.8851 9.30849 15.8328 9.22958 15.7666 9.16333C15.7003 9.09708 15.6214 9.04485 15.5346 9.00976C15.4477 8.97467 15.3546 8.95744 15.261 8.95909C15.1673 8.96075 15.0749 8.98125 14.9893 9.01938C14.9037 9.05751 14.8267 9.11249 14.7628 9.18104L10.6047 13.3392L9.23726 11.9717C9.105 11.8485 8.93008 11.7814 8.74933 11.7846C8.56859 11.7878 8.39613 11.861 8.26831 11.9888C8.14048 12.1167 8.06726 12.2891 8.06407 12.4699C8.06088 12.6506 8.12798 12.8255 8.25121 12.9578L10.1117 14.8182C10.2425 14.9489 10.4198 15.0223 10.6047 15.0223C10.7896 15.0223 10.9669 14.9489 11.0977 14.8182L15.7489 10.1671Z" fill="#10B981"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z" fill="#10B981"/>
                                      </svg>
                                    </button>
                                    <button className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`} title="Cancel" onClick={() => { setEditingServiceId(null); setEditServiceData({}); }}>
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10.1676 9.18104C10.0353 9.0578 9.86039 8.99071 9.67965 8.9939C9.4989 8.99709 9.32645 9.07031 9.19862 9.19814C9.0708 9.32596 8.99758 9.49841 8.99439 9.67916C8.9912 9.8599 9.05829 10.0348 9.18153 10.1671L11.0141 11.9996L9.18153 13.8322C9.11298 13.8961 9.058 13.9731 9.01987 14.0587C8.98174 14.1443 8.96123 14.2366 8.95958 14.3303C8.95793 14.424 8.97516 14.517 9.01025 14.6039C9.04534 14.6908 9.09757 14.7697 9.16382 14.836C9.23007 14.9022 9.30898 14.9544 9.39585 14.9895C9.48273 15.0246 9.57578 15.0418 9.66945 15.0402C9.76313 15.0385 9.85552 15.018 9.9411 14.9799C10.0267 14.9418 10.1037 14.8868 10.1676 14.8182L12.0001 12.9857L13.8327 14.8182C13.9649 14.9415 14.1399 15.0086 14.3206 15.0054C14.5014 15.0022 14.6738 14.929 14.8016 14.8012C14.9295 14.6733 15.0027 14.5009 15.0059 14.3201C15.0091 14.1394 14.942 13.9645 14.8187 13.8322L12.9862 11.9996L14.8187 10.1671C14.8873 10.1032 14.9423 10.0262 14.9804 9.94061C15.0185 9.85503 15.039 9.76264 15.0407 9.66897C15.0423 9.57529 15.0251 9.48224 14.99 9.39537C14.9549 9.30849 14.9027 9.22958 14.8364 9.16333C14.7702 9.09708 14.6913 9.04485 14.6044 9.00976C14.5175 8.97467 14.4245 8.95744 14.3308 8.95909C14.2371 8.96075 14.1447 8.98125 14.0592 9.01938C13.9736 9.05751 13.8966 9.11249 13.8327 9.18104L12.0001 11.0136L10.1676 9.18104Z" fill="#EF4444"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z" fill="#EF4444"/>
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td>{item.testName}</td>
                                <td>{(assessmentMode && !orderDetails?.sessions_updated_by_therapist && !isOnSiteLocation) ? '—' : item.numberOfSessions}</td>
                                <td>{item.sessionsCompleted}</td>
                                <td>{(assessmentMode && !orderDetails?.sessions_updated_by_therapist && !isOnSiteLocation) ? '—' : item.sessionsRemaining}</td>
                                <td><span className={`${styles["db-status"]} ${styles[getStatusColor(item.location)]}`}>{item.location}</span></td>
                                <td>IQD {formatAmount(item.price)}</td>
                                <td>
                                  <div className={styles["apt-action-buttons"]}>
                                    <button className={styles["apt-view-btn"]} title="Edit" disabled={(orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)} style={{ opacity: ((orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)) ? 0.4 : 1, cursor: ((orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)) ? 'not-allowed' : 'pointer' }} onClick={() => { if ((orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)) return; setEditingServiceId(orderDetails.services[idx]?.id); setEditServiceData({ number_of_sessions: String(orderDetails.services[idx]?.number_of_sessions || 1), location: orderDetails.services[idx]?.location || "ON_SITE", price: String(Math.round(Number(orderDetails.services[idx]?.price || 0))) }); }}><EditIcon /></button>
                                    <button className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`} title="Delete" disabled={(orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)} style={{ opacity: ((orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)) ? 0.4 : 1, cursor: ((orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)) ? 'not-allowed' : 'pointer' }} onClick={() => { if ((orderDetails?.status !== 'REQUEST_SUBMITTED' && !(orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) || isPostSplitConfirmation || (item.sessionsCompleted >= item.numberOfSessions)) return; handleDeleteService(orderDetails.services[idx]?.id); }}><Trash2 size={24} /></button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        {showAddTherapyRow && (
                          <tr>
                            <td>
                              <ServiceSearchSelect
                                services={servicesCatalog.filter(svc => !(orderDetails?.services || []).some(existing => existing.service_name === svc.test_name))}
                                value={newService.serviceName}
                                valueKey="test_name"
                                onSelect={(testName, selected) => {
                                  setNewService({
                                    ...newService,
                                    serviceName: testName || "",
                                    serviceCode: selected?.service_code || "",
                                    price: selected?.price ? String(Math.round(Number(selected.price))) : "",
                                    // Location follows the order's effective (preferred) location,
                                    // not the service's own catalog location — mirrors the lab app
                                    // so an added service always shows the preferred location.
                                    location: getEffectiveLocation(),
                                  });
                                }}
                                placeholder="Search by service name or code"
                              />
                            </td>
                            <td>{(assessmentMode && !isOnSiteLocation) ? <span style={{ color: '#9ca3af' }}>—</span> : <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="1" value={newService.numberOfSessions} onChange={(e) => setNewService({...newService, numberOfSessions: e.target.value.replace(/[^0-9]/g, "")})} className={styles["apt-field-input"]} style={{width:"100px", padding:"6px 10px"}} />}</td>
                            <td>0</td>
                            <td>{(assessmentMode && !isOnSiteLocation) ? '—' : (newService.numberOfSessions || 1)}</td>
                            <td>
                              {/* For an ASSESSMENT order the service location is fixed to the
                                  patient's preferred location and cannot be changed: On-Site →
                                  locked On-Site, Home Visit → locked Home Visit. */}
                              <select className={styles["apt-status-filter"]} value={assessmentMode ? (isOnSiteLocation ? 'ON_SITE' : 'HOME_VISIT') : newService.location} onChange={(e) => setNewService({...newService, location: e.target.value})} disabled={assessmentMode}>
                                {(!assessmentMode || isOnSiteLocation) && allowedLocations.includes(LOCATION_CODES.ON_SITE) && <option value="ON_SITE">On-Site</option>}
                                {(!assessmentMode || !isOnSiteLocation) && allowedLocations.includes(LOCATION_CODES.HOME_VISIT) && <option value="HOME_VISIT">Home Visit</option>}
                              </select>
                            </td>
                            <td><AmountInput placeholder="IQD" value={newService.price} onValueChange={(value) => setNewService({...newService, price: value})} className={styles["apt-field-input"]} style={{width:"100px", padding:"6px 10px"}} /></td>
                            <td>
                              <div className={styles["apt-action-buttons"]}>
                                <button className={styles["apt-view-btn"]} title="Save" onClick={() => handleAddService()}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.7489 10.1671C15.8174 10.1032 15.8724 10.0262 15.9105 9.94061C15.9487 9.85503 15.9692 9.76264 15.9708 9.66897C15.9725 9.57529 15.9553 9.48224 15.9202 9.39537C15.8851 9.30849 15.8328 9.22958 15.7666 9.16333C15.7003 9.09708 15.6214 9.04485 15.5346 9.00976C15.4477 8.97467 15.3546 8.95744 15.261 8.95909C15.1673 8.96075 15.0749 8.98125 14.9893 9.01938C14.9037 9.05751 14.8267 9.11249 14.7628 9.18104L10.6047 13.3392L9.23726 11.9717C9.105 11.8485 8.93008 11.7814 8.74933 11.7846C8.56859 11.7878 8.39613 11.861 8.26831 11.9888C8.14048 12.1167 8.06726 12.2891 8.06407 12.4699C8.06088 12.6506 8.12798 12.8255 8.25121 12.9578L10.1117 14.8182C10.2425 14.9489 10.4198 15.0223 10.6047 15.0223C10.7896 15.0223 10.9669 14.9489 11.0977 14.8182L15.7489 10.1671Z" fill="#10B981"/><path fillRule="evenodd" clipRule="evenodd" d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z" fill="#10B981"/></svg></button><button className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`} title="Cancel" onClick={() => { setShowAddTherapyRow(false); setNewService({ serviceName: "", numberOfSessions: 1, location: "ON_SITE", price: "" }); }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.1676 9.18104C10.0353 9.0578 9.86039 8.99071 9.67965 8.9939C9.4989 8.99709 9.32645 9.07031 9.19862 9.19814C9.0708 9.32596 8.99758 9.49841 8.99439 9.67916C8.9912 9.8599 9.05829 10.0348 9.18153 10.1671L11.0141 11.9996L9.18153 13.8322C9.11298 13.8961 9.058 13.9731 9.01987 14.0587C8.98174 14.1443 8.96123 14.2366 8.95958 14.3303C8.95793 14.424 8.97516 14.517 9.01025 14.6039C9.04534 14.6908 9.09757 14.7697 9.16382 14.836C9.23007 14.9022 9.30898 14.9544 9.39585 14.9895C9.48273 15.0246 9.57578 15.0418 9.66945 15.0402C9.76313 15.0385 9.85552 15.018 9.9411 14.9799C10.0267 14.9418 10.1037 14.8868 10.1676 14.8182L12.0001 12.9857L13.8327 14.8182C13.9649 14.9415 14.1399 15.0086 14.3206 15.0054C14.5014 15.0022 14.6738 14.929 14.8016 14.8012C14.9295 14.6733 15.0027 14.5009 15.0059 14.3201C15.0091 14.1394 14.942 13.9645 14.8187 13.8322L12.9862 11.9996L14.8187 10.1671C14.8873 10.1032 14.9423 10.0262 14.9804 9.94061C15.0185 9.85503 15.039 9.76264 15.0407 9.66897C15.0423 9.57529 15.0251 9.48224 14.99 9.39537C14.9549 9.30849 14.9027 9.22958 14.8364 9.16333C14.7702 9.09708 14.6913 9.04485 14.6044 9.00976C14.5175 8.97467 14.4245 8.95744 14.3308 8.95909C14.2371 8.96075 14.1447 8.98125 14.0592 9.01938C13.9736 9.05751 13.8966 9.11249 13.8327 9.18104L12.0001 11.0136L10.1676 9.18104Z" fill="#EF4444"/><path fillRule="evenodd" clipRule="evenodd" d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z" fill="#EF4444"/></svg></button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={5}>Total</td>
                          <td colSpan={2}>IQD {orderDetails?.services ? formatWholeAmount(orderDetails.services.reduce((sum, s) => sum + Number(s.price || 0), 0)) : "0"}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
              ) : (
              <div className={styles["white-card"]}>
                <div className={styles["patient-tab-title"]}>
                  <h2>Therapy Details</h2>
                  {((orderDetails?.status === 'REQUEST_SUBMITTED' && isPhysioAccepted) || (orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation)) && !isPostSplitConfirmation && (
                  <Link href="" className={styles["add-test-link"]} onClick={(e) => { e.preventDefault(); setShowAddTherapyRow(true); }}>
                    <FaCirclePlus /> Add Therapy
                  </Link>
                  )}
                </div>
                <div className={styles["bg-gray-light"] + " " + "p-3 rounded-2 text-center text-secondary fw-semibold"}>
                  {showAddTherapyRow ? (
                    <div className={`${styles["apt-appointments-table"]} ${styles["test-details-table"]}`}>
                      <table>
                        <thead>
                          <tr>
                            <th>Therapy Name</th>
                            <th>Number of Sessions</th>
                            <th>Location</th>
                            <th>Price</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <ServiceSearchSelect
                                services={servicesCatalog.filter(svc => !(orderDetails?.services || []).some(existing => existing.service_name === svc.test_name))}
                                value={newService.serviceName}
                                valueKey="test_name"
                                onSelect={(testName, selected) => {
                                  setNewService({
                                    ...newService,
                                    serviceName: testName || "",
                                    serviceCode: selected?.service_code || "",
                                    price: selected?.price ? String(Math.round(Number(selected.price))) : "",
                                    // Location follows the order's effective (preferred) location,
                                    // not the service's own catalog location — mirrors the lab app
                                    // so an added service always shows the preferred location.
                                    location: getEffectiveLocation(),
                                  });
                                }}
                                placeholder="Search by service name or code"
                              />
                            </td>
                            <td>{(assessmentMode && !isOnSiteLocation) ? <span style={{ color: '#9ca3af' }}>—</span> : <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="1" value={newService.numberOfSessions} onChange={(e) => setNewService({...newService, numberOfSessions: e.target.value.replace(/[^0-9]/g, "")})} className={styles["apt-field-input"]} style={{width:"100px", padding:"6px 10px"}} />}</td>
                            <td>
                              {/* For an ASSESSMENT order the service location is fixed to the
                                  patient's preferred location and cannot be changed: On-Site →
                                  locked On-Site, Home Visit → locked Home Visit. */}
                              <select className={styles["apt-status-filter"]} value={assessmentMode ? (isOnSiteLocation ? 'ON_SITE' : 'HOME_VISIT') : newService.location} onChange={(e) => setNewService({...newService, location: e.target.value})} disabled={assessmentMode}>
                                {(!assessmentMode || isOnSiteLocation) && allowedLocations.includes(LOCATION_CODES.ON_SITE) && <option value="ON_SITE">On-Site</option>}
                                {(!assessmentMode || !isOnSiteLocation) && allowedLocations.includes(LOCATION_CODES.HOME_VISIT) && <option value="HOME_VISIT">Home Visit</option>}
                              </select>
                            </td>
                            <td><AmountInput placeholder="IQD" value={newService.price} onValueChange={(value) => setNewService({...newService, price: value})} className={styles["apt-field-input"]} style={{width:"100px", padding:"6px 10px"}} /></td>
                            <td>
                              <div className={styles["apt-action-buttons"]}>
                                <button className={styles["apt-view-btn"]} title="Save" onClick={() => handleAddService()}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.7489 10.1671C15.8174 10.1032 15.8724 10.0262 15.9105 9.94061C15.9487 9.85503 15.9692 9.76264 15.9708 9.66897C15.9725 9.57529 15.9553 9.48224 15.9202 9.39537C15.8851 9.30849 15.8328 9.22958 15.7666 9.16333C15.7003 9.09708 15.6214 9.04485 15.5346 9.00976C15.4477 8.97467 15.3546 8.95744 15.261 8.95909C15.1673 8.96075 15.0749 8.98125 14.9893 9.01938C14.9037 9.05751 14.8267 9.11249 14.7628 9.18104L10.6047 13.3392L9.23726 11.9717C9.105 11.8485 8.93008 11.7814 8.74933 11.7846C8.56859 11.7878 8.39613 11.861 8.26831 11.9888C8.14048 12.1167 8.06726 12.2891 8.06407 12.4699C8.06088 12.6506 8.12798 12.8255 8.25121 12.9578L10.1117 14.8182C10.2425 14.9489 10.4198 15.0223 10.6047 15.0223C10.7896 15.0223 10.9669 14.9489 11.0977 14.8182L15.7489 10.1671Z" fill="#10B981"/><path fillRule="evenodd" clipRule="evenodd" d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z" fill="#10B981"/></svg></button><button className={`${styles["apt-view-btn"]} ${styles["delete-color"]}`} title="Cancel" onClick={() => { setShowAddTherapyRow(false); setNewService({ serviceName: "", numberOfSessions: 1, location: "ON_SITE", price: "" }); }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.1676 9.18104C10.0353 9.0578 9.86039 8.99071 9.67965 8.9939C9.4989 8.99709 9.32645 9.07031 9.19862 9.19814C9.0708 9.32596 8.99758 9.49841 8.99439 9.67916C8.9912 9.8599 9.05829 10.0348 9.18153 10.1671L11.0141 11.9996L9.18153 13.8322C9.11298 13.8961 9.058 13.9731 9.01987 14.0587C8.98174 14.1443 8.96123 14.2366 8.95958 14.3303C8.95793 14.424 8.97516 14.517 9.01025 14.6039C9.04534 14.6908 9.09757 14.7697 9.16382 14.836C9.23007 14.9022 9.30898 14.9544 9.39585 14.9895C9.48273 15.0246 9.57578 15.0418 9.66945 15.0402C9.76313 15.0385 9.85552 15.018 9.9411 14.9799C10.0267 14.9418 10.1037 14.8868 10.1676 14.8182L12.0001 12.9857L13.8327 14.8182C13.9649 14.9415 14.1399 15.0086 14.3206 15.0054C14.5014 15.0022 14.6738 14.929 14.8016 14.8012C14.9295 14.6733 15.0027 14.5009 15.0059 14.3201C15.0091 14.1394 14.942 13.9645 14.8187 13.8322L12.9862 11.9996L14.8187 10.1671C14.8873 10.1032 14.9423 10.0262 14.9804 9.94061C15.0185 9.85503 15.039 9.76264 15.0407 9.66897C15.0423 9.57529 15.0251 9.48224 14.99 9.39537C14.9549 9.30849 14.9027 9.22958 14.8364 9.16333C14.7702 9.09708 14.6913 9.04485 14.6044 9.00976C14.5175 8.97467 14.4245 8.95744 14.3308 8.95909C14.2371 8.96075 14.1447 8.98125 14.0592 9.01938C13.9736 9.05751 13.8966 9.11249 13.8327 9.18104L12.0001 11.0136L10.1676 9.18104Z" fill="#EF4444"/><path fillRule="evenodd" clipRule="evenodd" d="M12.053 2H11.947C9.79907 2 8.11628 2 6.80279 2.17674C5.45954 2.35721 4.39907 2.73488 3.56651 3.56651C2.73395 4.39907 2.35721 5.45953 2.17674 6.80372C2 8.11628 2 9.79907 2 11.947V12.053C2 14.2009 2 15.8837 2.17674 17.1972C2.35721 18.5405 2.73488 19.6009 3.56651 20.4335C4.39907 21.266 5.45953 21.6428 6.80372 21.8233C8.11628 22 9.79907 22 11.947 22H12.053C14.2009 22 15.8837 22 17.1972 21.8233C18.5405 21.6428 19.6009 21.2651 20.4335 20.4335C21.266 19.6009 21.6428 18.5405 21.8233 17.1963C22 15.8837 22 14.2009 22 12.053V11.947C22 9.79907 22 8.11628 21.8233 6.80279C21.6428 5.45954 21.2651 4.39907 20.4335 3.56651C19.6009 2.73395 18.5405 2.35721 17.1963 2.17674C15.8837 2 14.2009 2 12.053 2ZM4.55349 4.55349C5.08372 4.02326 5.8 3.71907 6.98977 3.55907C8.19907 3.39721 9.78791 3.39535 12 3.39535C14.2121 3.39535 15.8009 3.39721 17.0102 3.55907C18.2 3.71907 18.9172 4.02419 19.4474 4.55349C19.9767 5.08372 20.2809 5.8 20.4409 6.98977C20.6028 8.19907 20.6047 9.78791 20.6047 12C20.6047 14.2121 20.6028 15.8009 20.4409 17.0102C20.2809 18.2 19.9758 18.9172 19.4465 19.4474C18.9163 19.9767 18.2 20.2809 17.0102 20.4409C15.8009 20.6028 14.2121 20.6047 12 20.6047C9.78791 20.6047 8.19907 20.6028 6.98977 20.4409C5.8 20.2809 5.08279 19.9758 4.55256 19.4465C4.02326 18.9163 3.71907 18.2 3.55907 17.0102C3.39721 15.8009 3.39535 14.2121 3.39535 12C3.39535 9.78791 3.39721 8.19907 3.55907 6.98977C3.71907 5.8 4.02419 5.08372 4.55349 4.55349Z" fill="#EF4444"/></svg></button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    "Therapy Not Selected"
                  )}
                </div>
              </div>
              )}

              {/* Transportation Fee - only show for Home Visit services when order is editable */}
              {(() => {
                const services = orderDetails?.services || [];
                const hasHomeVisitServices = services.some(s => s.location === 'HOME_VISIT');
                // Editable (officer can add/edit the transportation fee) when the order is
                // newly received, an on-site assessment order, OR a home-visit
                // assessment order whose therapist has already submitted the
                // assessment (sessions_updated_by_therapist) — that's when the
                // officer reviews and prepares the invoice for the patient.
                const isEditable =
                  orderDetails?.status === 'REQUEST_SUBMITTED' ||
                  (orderDetails?.status === 'ASSESSMENT' &&
                    (isOnSiteLocation ||
                      orderDetails?.sessions_updated_by_therapist === true));
                if (!hasHomeVisitServices || !isEditable) return null;
                return (
                  <>
                    {!transportationFeeAdded && (
                      <div style={{ padding: "12px 0" }}>
                        <button
                          onClick={() => { setTempTransportationFee(orderTransportationFee === "0" ? "" : String(Math.round(Number(orderTransportationFee)))); setShowTransportationFeePopup(true); }}
                          style={{
                            background: "none", border: "2px dashed #099494", borderRadius: "8px",
                            padding: "10px 20px", color: "#099494", fontWeight: 600, fontSize: "14px",
                            cursor: "pointer", display: "flex", alignItems: "center", gap: "8px",
                          }}
                        >
                          + Add Transportation Fee
                        </button>
                      </div>
                    )}
                    {showTransportationFeePopup && (
                      <div className={dialogStyles["labDialog_wrapper"]}>
                        <div className={dialogStyles["dialog-overlay"]} style={{ alignItems: "center" }}>
                          <div className={dialogStyles["dialog-box"]} style={{ width: "460px" }}>
                            <button
                              className={dialogStyles["dialog-close"]}
                              onClick={() => setShowTransportationFeePopup(false)}
                            >
                              ×
                            </button>
                            <h2 className={dialogStyles["dialog-title"]}>
                              {transportationFeeAdded ? "Edit Transportation Fee" : "Add Transportation Fee"}
                            </h2>
                            <label style={{ fontSize: "14px", color: "#374151", fontWeight: 500, marginBottom: "6px", display: "block" }}>Transportation Fee (IQD)</label>
                            <input
                              type="text" inputMode="numeric" pattern="[0-9]*" min="0" placeholder="Enter transportation fee"
                              value={tempTransportationFee}
                              onChange={(e) => setTempTransportationFee(e.target.value.replace(/[^0-9]/g, ""))}
                              style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "12px", fontSize: "14px", outline: "none", marginBottom: "4px", boxSizing: "border-box" }}
                              autoFocus
                            />
                            <div className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}>
                              <button
                                className={dialogStyles["btn-outlined-1"]}
                                onClick={() => setShowTransportationFeePopup(false)}
                              >
                                Cancel
                              </button>
                              <button
                                className={dialogStyles["btn-1"]}
                                onClick={() => {
                                  // 0 IQD is a valid fee (free transport). Only an empty field
                                  // is refused, so "not entered" and "0" stay distinct.
                                  const raw = String(tempTransportationFee ?? "").trim();
                                  const fee = Number(raw);
                                  if (raw === "" || !Number.isFinite(fee) || fee < 0) {
                                    toast.error("Enter a transportation fee (0 IQD is allowed).");
                                    return;
                                  }
                                  setOrderTransportationFee(String(Math.round(fee)));
                                  setTransportationFeeAdded(true);
                                  setShowTransportationFeePopup(false);
                                }}
                                style={{ justifyContent: "center", border: "1px solid transparent", boxSizing: "border-box" }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  </>
                );
              })()}
              </>
              )}

              {/* Delete Service Confirmation Modal */}
              {showDeleteModal && (
                <div style={{
                  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
                }}>
                  <div style={{
                    backgroundColor: "#fff", borderRadius: "12px", padding: "24px", width: "380px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                  }}>
                    <div style={{ textAlign: "center", marginBottom: "16px" }}>
                      <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                        <Trash2 size={22} color="#EF4444" />
                      </div>
                      <h3 style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 600, color: "#1f2937" }}>
                        Remove Service
                      </h3>
                      <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
                        Are you sure you want to remove this service? This action cannot be undone.
                      </p>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "20px" }}>
                      <button
                        onClick={() => { setShowDeleteModal(false); setDeleteServiceId(null); }}
                        style={{ padding: "10px 24px", border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmDeleteService}
                        style={{ padding: "10px 24px", border: "none", borderRadius: "8px", background: "#EF4444", color: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Accept Order Confirmation Modal — styled like the Add New Service dialog */}
              {showAcceptModal && (
                <div className={dialogStyles["labDialog_wrapper"]}>
                  <div className={dialogStyles["dialog-overlay"]} style={{ alignItems: "center" }}>
                    <div className={dialogStyles["dialog-box"]} style={{ width: "460px" }}>
                      <button className={dialogStyles["dialog-close"]} onClick={() => setShowAcceptModal(false)} disabled={acceptingOrder}>×</button>
                      <h2 className={dialogStyles["dialog-title"]}>Accept Order</h2>
                      <p style={{ margin: "0 0 8px", fontSize: "14px", color: "#6b7280", lineHeight: 1.5 }}>
                        Accept this order? The patient will be notified and you can then add therapies and send the invoice.
                      </p>
                      <div className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}>
                        <button className={dialogStyles["btn-outlined-1"]} onClick={() => setShowAcceptModal(false)} disabled={acceptingOrder}>
                          Cancel
                        </button>
                        <button className={dialogStyles["btn-1"]} onClick={confirmAcceptOrder} disabled={acceptingOrder} style={{ opacity: acceptingOrder ? 0.6 : 1, justifyContent: "center", border: "1px solid transparent", boxSizing: "border-box" }}>
                          <FaCheck /> {acceptingOrder ? "Accepting..." : "Accept"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Reject Order Confirmation Modal — styled like the Add New Service dialog */}
              {showRejectModal && (
                <div className={dialogStyles["labDialog_wrapper"]}>
                  <div className={dialogStyles["dialog-overlay"]} style={{ alignItems: "center" }}>
                    <div className={dialogStyles["dialog-box"]} style={{ width: "460px" }}>
                      <button className={dialogStyles["dialog-close"]} onClick={() => { setShowRejectModal(false); resetRejectForm(); }} disabled={rejectingOrder}>×</button>
                      <h2 className={dialogStyles["dialog-title"]}>Reject Order</h2>
                      <p style={{ margin: "0 0 12px", fontSize: "14px", color: "#6b7280", lineHeight: 1.5 }}>
                        Reject this order? The patient will be notified. This will move the order to the Rejected tab.
                      </p>
                      <label style={{ fontSize: "14px", color: "#374151", fontWeight: 500, marginBottom: "6px", display: "block" }}>
                        Reason (optional)
                      </label>
                      {/* Admin-configured reasons for this center + an "Other"
                          escape hatch that reveals the free-text box below. The
                          reason text follows the app locale (Arabic when RTL). */}
                      <select
                        value={selectedRejectReasonId}
                        onChange={(e) => {
                          setSelectedRejectReasonId(e.target.value);
                          // Drop any half-typed custom text when switching to a preset.
                          if (e.target.value !== CUSTOM_REJECTION_REASON) setRejectReason("");
                        }}
                        disabled={rejectingOrder || loadingRejectReasons}
                        style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "12px", fontSize: "14px", outline: "none", marginBottom: isCustomRejectReason ? "10px" : "4px", boxSizing: "border-box", backgroundColor: "#fff", cursor: rejectingOrder ? "not-allowed" : "pointer" }}
                      >
                        <option value="">
                          {loadingRejectReasons ? "Loading..." : "Select a reason (optional)"}
                        </option>
                        {rejectReasonOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                        <option value={CUSTOM_REJECTION_REASON}>Other (type your own reason)</option>
                      </select>
                      {isCustomRejectReason && (
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Add a reason for rejecting this order..."
                          rows={3}
                          autoFocus
                          style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "12px", fontSize: "14px", outline: "none", resize: "vertical", marginBottom: "4px", boxSizing: "border-box" }}
                        />
                      )}
                      <div className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}>
                        <button className={dialogStyles["btn-outlined-1"]} onClick={() => { setShowRejectModal(false); resetRejectForm(); }} disabled={rejectingOrder}>
                          Cancel
                        </button>
                        <button className={dialogStyles["btn-1"]} onClick={confirmRejectOrder} disabled={rejectingOrder} style={{ opacity: rejectingOrder ? 0.6 : 1, justifyContent: "center", border: "1px solid transparent", boxSizing: "border-box" }}>
                          {rejectingOrder ? "Rejecting..." : "Reject"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showTherapyAndInvoiceSections && (
              <div className={styles["white-card"]}>
                <div className={styles["patient-tab-title"]}>
                  <div className="d-flex align-items-center gap-2 gap-lg-4 pb-2 flex-md-nowrap flex-wrap">
                  <h2 className="m-0">Invoice</h2>
                  {(() => {
                    const allSvcs = orderDetails?.services || [];
                    const isRevisit = (orderDetails?.current_session_number || 1) > 1;
                    const hasActive = isRevisit && allSvcs.some(s => (s.sessions_completed || 0) < (s.number_of_sessions || 1));
                    const allDone = isRevisit && !hasActive;
                    if (allDone) {
                      return <div className={styles["sessionTag"]}>All Sessions Complete</div>;
                    }
                    return <div className={styles["sessionTag"]}>Session {orderDetails?.current_session_number || 1}</div>;
                  })()}
                  </div>
                </div>
                {/* A revisit awaiting payment is shown by the blue "Invoice sent"
                    panel at the top of the Order Details tab. */}
                {(() => {
                  const allServices = orderDetails?.services || [];
                  const isRevisitOrder = (orderDetails?.current_session_number || 1) > 1;
                  const activeServices = isRevisitOrder 
                    ? allServices.filter(s => (s.sessions_completed || 0) < (s.number_of_sessions || 1))
                    : allServices;
                  // Check if ALL services have completed ALL their sessions (truly done, no more revisits possible)
                  const allServicesFullyDone = isRevisitOrder && allServices.length > 0 && allServices.every(s => (s.sessions_completed || 0) >= (s.number_of_sessions || 1));
                  // Show session breakdown when all services are fully done (regardless of status)
                  // OR when active services are empty and status is not COMPLETED (mid-flow state)
                  const allSessionsComplete = allServicesFullyDone || (isRevisitOrder && activeServices.length === 0 && orderDetails?.status !== 'COMPLETED');
                  const sessionHistory = orderDetails?.session_history || [];

                  // When all sessions are complete, show each session separately
                  if (allSessionsComplete && sessionHistory.length > 0) {
                    const totalSessions = orderDetails?.current_session_number || 1;
                    const feePercentage = Number(orderDetails?.admin_fee_percentage) || 0;
                    const storedTransportationFee = Number(orderDetails?.transportation_fee || 0);

                    return (
                      <>
                        {Array.from({ length: totalSessions }, (_, i) => i + 1).map((sessionNum) => {
                          // Services active in this session = those with number_of_sessions >= sessionNum
                          const sessionServices = allServices.filter(s => (s.number_of_sessions || 1) >= sessionNum);
                          const sessionSubtotal = sessionServices.reduce((sum, s) => sum + Number(s.price || 0), 0);
                          const sessionHasHomeVisit = sessionServices.some(s => s.location === 'HOME_VISIT');
                          const sessionTransportationFee = sessionHasHomeVisit ? storedTransportationFee : 0;
                          const sessionAdminFee = (sessionSubtotal + sessionTransportationFee) * feePercentage / 100;
                          const sessionTotal = sessionSubtotal + sessionTransportationFee + sessionAdminFee;

                          // Get therapist/date info from session history
                          const historyEntry = sessionHistory.find(h => h.session_number === sessionNum);

                          return (
                            <div key={`session-${sessionNum}`} style={{ marginBottom: '20px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>Session {sessionNum}</span>
                                <span style={{ fontSize: '11px', color: '#065f46', backgroundColor: '#d1fae5', padding: '2px 8px', borderRadius: '4px' }}>Completed</span>
                                {historyEntry?.therapist_name && <span style={{ fontSize: '12px', color: '#6b7280' }}>• {historyEntry.therapist_name}</span>}
                                {historyEntry?.scheduled_date && <span style={{ fontSize: '12px', color: '#6b7280' }}>• {(() => { const parts = (historyEntry.scheduled_date || '').split('-'); return parts.length === 3 ? tzLocale(new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])), 'en-GB', undefined) : historyEntry.scheduled_date; })()}</span>}
                              </div>
                              {/* Service names for this session */}
                              {sessionServices.map((svc, svcIdx) => (
                                <div className={styles["test-row"]} key={svcIdx}>
                                  <span className={styles["test-label"]}>
                                    {svc.service_name}
                                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666', backgroundColor: svc.location === 'HOME_VISIT' ? '#E8F0FF' : '#F3E8FF', padding: '2px 8px', borderRadius: '4px' }}>
                                      {svc.location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site'}
                                    </span>
                                  </span>
                                  <span className={styles["test-value-strong"]}>IQD {formatWholeAmount(svc.price)}</span>
                                </div>
                              ))}
                              <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #eee' }} />
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>Subtotal</span>
                                <span className={styles["test-value-strong"]}>IQD {formatWholeAmount(sessionSubtotal)}</span>
                              </div>
                              {sessionHasHomeVisit && (
                                <div className={styles["test-row"]}>
                                  <span className={styles["test-label"]}>Transportation Fee</span>
                                  <span className={styles["test-value-strong"]}>{sessionTransportationFee > 0 ? `IQD ${formatWholeAmount(sessionTransportationFee)}` : t("physioAppointmentDetail.free", "Free")}</span>
                                </div>
                              )}
                              {feePercentage > 0 && (
                                <div className={styles["test-row"]}>
                                  <span className={styles["test-label"]}>Admin Fee ({formatPercent(feePercentage)}%)</span>
                                  <span className={styles["test-value-strong"]}>IQD {formatWholeAmount(sessionAdminFee)}</span>
                                </div>
                              )}
                              <div className={styles["test-row"] + " " + styles["total-price-row"]}>
                                <span className={styles["test-value-strong"]}>Total</span>
                                <span className={styles["test-value-strong"]}><strong>IQD {formatWholeAmount(sessionTotal)}</strong></span>
                              </div>
                              {sessionNum < totalSessions && <hr style={{ margin: '16px 0', border: 'none', borderTop: '2px dashed #e5e7eb' }} />}
                            </div>
                          );
                        })}
                      </>
                    );
                  }

                  // Normal display (not all sessions complete)
                  return null;
                })()}
                {/* Normal invoice display (when NOT all sessions complete) */}
                {(() => {
                  const allServices = orderDetails?.services || [];
                  const isRevisitOrder = (orderDetails?.current_session_number || 1) > 1;
                  const activeServices = isRevisitOrder 
                    ? allServices.filter(s => (s.sessions_completed || 0) < (s.number_of_sessions || 1))
                    : allServices;
                  const allServicesFullyDone = isRevisitOrder && allServices.length > 0 && allServices.every(s => (s.sessions_completed || 0) >= (s.number_of_sessions || 1));
                  const allSessionsComplete = allServicesFullyDone || (isRevisitOrder && activeServices.length === 0 && orderDetails?.status !== 'COMPLETED');
                  const sessionHistory = orderDetails?.session_history || [];
                  if (allSessionsComplete && sessionHistory.length > 0) return null;

                  return orderDetails?.services && orderDetails.services.length > 0 ? (
                  <>
                    {orderDetails.services.map((svc, idx) => {
                      const isServiceCompleted = isRevisitOrder && (svc.sessions_completed || 0) >= (svc.number_of_sessions || 1);
                      // When COMPLETED + revisit, only show services that were active in this session
                      const isCompletedRevisit = orderDetails?.status === 'COMPLETED' && isRevisitOrder;
                      const wasActiveInCurrentSession = (svc.number_of_sessions || 1) >= (orderDetails?.current_session_number || 1);
                      if (isCompletedRevisit && !wasActiveInCurrentSession) return null;
                      return (
                      <div className={styles["test-row"]} key={idx} style={isServiceCompleted ? { opacity: 0.5 } : {}}>
                        <div>
                          <span className={styles["test-label"]}>
                            {svc.service_name}
                            <span style={{ 
                              marginLeft: '8px', 
                              fontSize: '12px', 
                              color: '#666',
                              backgroundColor: svc.location === 'HOME_VISIT' ? '#E8F0FF' : '#F3E8FF',
                              padding: '2px 8px',
                              borderRadius: '4px'
                            }}>
                              {svc.location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site'}
                            </span>
                            {isServiceCompleted && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', color: '#065f46', backgroundColor: '#d1fae5', padding: '2px 8px', borderRadius: '4px' }}>
                                Sessions Complete
                              </span>
                            )}
                          </span>
                        </div>
                        <span className={styles["test-value-strong"]} style={isServiceCompleted ? { textDecoration: 'line-through', color: '#9ca3af' } : {}}>
                          IQD {formatWholeAmount(svc.price)}
                        </span>
                      </div>
                      );
                    })}
                    
                    <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #eee' }} />
                    
                    {(() => {
                      // Only filter active services for revisit orders (session > 1)
                      const allServices = orderDetails?.services || [];
                      const isRevisitOrder = (orderDetails?.current_session_number || 1) > 1;
                      const isCompleted = orderDetails?.status === 'COMPLETED';
                      const currentSessionNum = orderDetails?.current_session_number || 1;
                      
                      // When order is COMPLETED and it's a revisit order, show what was paid in the current session
                      // Services active in this session = those with number_of_sessions >= current_session_number
                      if (isCompleted && isRevisitOrder) {
                        const sessionActiveServices = allServices.filter(s => (s.number_of_sessions || 1) >= currentSessionNum);
                        const sessionSubtotal = sessionActiveServices.reduce((sum, s) => sum + Number(s.price || 0), 0);
                        const sessionHasHomeVisit = sessionActiveServices.some(s => s.location === 'HOME_VISIT');
                        const sessionTransportationFee = sessionHasHomeVisit ? Number(orderDetails?.transportation_fee || 0) : 0;
                        const sessionFeePercentage = Number(orderDetails?.admin_fee_percentage || 0);
                        const sessionAdminFee = (sessionSubtotal + sessionTransportationFee) * sessionFeePercentage / 100;
                        const sessionTotal = sessionSubtotal + sessionTransportationFee + sessionAdminFee;
                        return (
                          <>
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>Subtotal</span>
                              <span className={styles["test-value-strong"]}>IQD {formatWholeAmount(sessionSubtotal)}</span>
                            </div>
                            {sessionHasHomeVisit && (
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>Transportation Fee</span>
                                <span className={styles["test-value-strong"]}>{sessionTransportationFee > 0 ? `IQD ${formatWholeAmount(sessionTransportationFee)}` : t("physioAppointmentDetail.free", "Free")}</span>
                              </div>
                            )}
                            {sessionFeePercentage > 0 && (
                              <div className={styles["test-row"]}>
                                <span className={styles["test-label"]}>Admin Fee ({formatPercent(sessionFeePercentage)}%)</span>
                                <span className={styles["test-value-strong"]}>IQD {formatWholeAmount(sessionAdminFee)}</span>
                              </div>
                            )}
                            <div className={styles["test-row"] + " " + styles["total-price-row"]}>
                              <span className={styles["test-value-strong"]}>Total Price</span>
                              <span className={styles["test-value-strong"]}><strong>IQD {formatWholeAmount(sessionTotal)}</strong></span>
                            </div>
                          </>
                        );
                      }

                      const activeServices = isRevisitOrder 
                        ? allServices.filter(s => (s.sessions_completed || 0) < (s.number_of_sessions || 1))
                        : allServices;
                      // If all services are fully done, show all services with full totals
                      const allServicesFullyDone2 = isRevisitOrder && allServices.length > 0 && allServices.every(s => (s.sessions_completed || 0) >= (s.number_of_sessions || 1));
                      const allSessionsComplete = allServicesFullyDone2 || (isRevisitOrder && activeServices.length === 0 && orderDetails?.status !== 'COMPLETED');
                      const displayServices = allSessionsComplete ? allServices : activeServices;
                      const hasCompletedServices = isRevisitOrder && !allSessionsComplete && activeServices.length < allServices.length;
                      const servicesTotal = displayServices.reduce((sum, s) => sum + Number(s.price || 0), 0);
                      
                      // Check if transportation fee should apply
                      const displayHasHomeVisit = displayServices.some(s => s.location === 'HOME_VISIT');
                      let transportationFee = 0;
                      if (isRevisitOrder && !allSessionsComplete) {
                        // For revisit with remaining sessions: only if active services have home visit
                        if (displayHasHomeVisit) {
                          transportationFee = (transportationFeeAdded && Number(orderTransportationFee) > 0) ? Number(orderTransportationFee) : (Number(orderDetails?.transportation_fee) > 0 ? Number(orderDetails.transportation_fee) : 0);
                        }
                      } else {
                        // For normal orders or all sessions complete: use transportation fee as-is
                        transportationFee = (transportationFeeAdded && Number(orderTransportationFee) > 0) ? Number(orderTransportationFee) : (Number(orderDetails?.transportation_fee) > 0 ? Number(orderDetails.transportation_fee) : 0);
                      }
                      
                      // Convert admin_fee_percentage from DB to number; fall back to fetched adminFeePercentage state
                      const orderFeePercentage = Number(orderDetails?.admin_fee_percentage) || 0;
                      const feePercentage = orderFeePercentage > 0 ? orderFeePercentage : (Number(adminFeePercentage) || 0);
                      // Admin fee is calculated on (subtotal + transportationFee) - always recalculate if there are completed services
                      const adminFeeBase = servicesTotal + transportationFee;
                      const orderAdminFeeAmount = Number(orderDetails?.admin_fee_amount) || 0;
                      const adminFee = (hasCompletedServices || orderAdminFeeAmount === 0) ? (adminFeeBase * feePercentage / 100) : orderAdminFeeAmount;
                      // Loyalty points redeemed (persisted on the booking) and the promo code
                      // discount (gateway enrichment), deducted on their own rows exactly as on
                      // the printed PhysioInvoiceModal — this card used to show the GROSS as the
                      // Total. Only for the first session: the booking keeps a single loyalty
                      // value, so on a revisit it may belong to an earlier session.
                      const loyaltyDiscount = !isRevisitOrder ? Number(orderDetails?.loyalty_discount) || 0 : 0;
                      const loyaltyPointsUsed = !isRevisitOrder ? Number(orderDetails?.loyalty_points_used) || 0 : 0;
                      const promoDiscount = !isRevisitOrder ? Number(orderDetails?.promo_discount) || 0 : 0;
                      const promoCode = orderDetails?.promo_code || null;
                      const total = Math.max(0, servicesTotal + transportationFee + adminFee - loyaltyDiscount - promoDiscount);
                      
                      return (
                        <>
                          <div className={styles["test-row"]}>
                            <span className={styles["test-label"]}>Subtotal</span>
                            <span className={styles["test-value-strong"]}>
                              IQD {formatWholeAmount(servicesTotal)}
                            </span>
                          </div>
                          
                          {/* Shown for an added 0 IQD fee too, so it can still be edited or removed,
                              and for a home visit once the invoice has been sent (a 0 fee reads "Free"). */}
                          {(transportationFee > 0 ||
                            (displayHasHomeVisit &&
                              (transportationFeeAdded ||
                                !['REQUEST_SUBMITTED', 'ASSESSMENT', 'REJECTED'].includes(orderDetails?.status)))) && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                Transportation Fee
                                {(orderDetails?.status === 'REQUEST_SUBMITTED' ||
                                  (orderDetails?.status === 'ASSESSMENT' &&
                                    (isOnSiteLocation ||
                                      orderDetails?.sessions_updated_by_therapist === true))) && (
                                  <>
                                    <button onClick={() => { setTempTransportationFee(String(Math.round(Number(orderTransportationFee)))); setShowTransportationFeePopup(true); }} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: "#099494" }} title="Edit">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    </button>
                                    <button onClick={() => { setOrderTransportationFee("0"); setTransportationFeeAdded(false); }} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: "#EF4444" }} title="Remove">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    </button>
                                  </>
                                )}
                              </span>
                              <span className={styles["test-value-strong"]}>
                                {transportationFee > 0
                                  ? `IQD ${formatWholeAmount(transportationFee)}`
                                  : t("physioAppointmentDetail.free", "Free")}
                              </span>
                            </div>
                          )}
                          
                          {feePercentage > 0 && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                Admin Fee ({formatPercent(feePercentage)}%)
                              </span>
                              <span className={styles["test-value-strong"]}>
                                IQD {formatWholeAmount(adminFee)}
                              </span>
                            </div>
                          )}

                          {loyaltyDiscount > 0 && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                Loyalty Point Discount{loyaltyPointsUsed > 0 ? ` (${loyaltyPointsUsed} pts)` : ""}
                              </span>
                              <span className={styles["test-value-strong"]} style={{ color: "#00BC7D" }}>
                                − IQD {formatWholeAmount(loyaltyDiscount)}
                              </span>
                            </div>
                          )}

                          {promoDiscount > 0 && (
                            <div className={styles["test-row"]}>
                              <span className={styles["test-label"]}>
                                Promo Discount{promoCode ? ` (${promoCode})` : ""}
                              </span>
                              <span className={styles["test-value-strong"]} style={{ color: "#00BC7D" }}>
                                − IQD {formatWholeAmount(promoDiscount)}
                              </span>
                            </div>
                          )}
                          
                          <div className={styles["test-row"] + " " + styles["total-price-row"]}>
                            <span className={styles["test-value-strong"]}>
                              Total Price
                            </span>
                            <span className={styles["test-value-strong"]}>
                              <strong>IQD {formatWholeAmount(total)}</strong>
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <div className="p-3 text-center text-secondary">No services added yet</div>
                )
                })()}
              </div>
              )}

              <div className="text-center d-flex flex-column align-items-center gap-2">
                {(() => {
                  // First-received order: the center must Accept or Reject before it
                  // can add therapies / send the invoice. Accept keeps the status as
                  // REQUEST_SUBMITTED (only flips physio_accepted); Reject → REJECTED.
                  // The Accept / Reject buttons live in the "Acceptance required"
                  // panel at the top of the Order Details tab, so nothing renders here.
                  if (isAwaitingCenterDecision) {
                    return null;
                  }

                  // Rejected order: explained by the red "Order rejected" panel (with
                  // the reason) at the top of the Order Details tab, so nothing here.
                  if (isOrderRejected) {
                    return null;
                  }

                  const services = orderDetails?.services || [];
                  const hasHomeVisit = services.some(s => s.location === 'HOME_VISIT');
                  // Order type is driven by the ACTUAL service location (services'
                  // location), not the patient's original preferred_location — the
                  // service location may have been changed to On-Site.
                  const isHomeVisitOrder = isHomeVisitService;
                  const isOnSiteOrder = !isHomeVisitOrder;
                  // Only require transportation fee if preferred location is home visit AND there are home visit services
                  // A fee of 0 IQD is valid. The stored column defaults to 0, so a stored 0 only
                  // counts as "decided" once a therapist has been assigned: assigning one already
                  // required the fee and saved it (assign-therapist sends transportationFee).
                  const needsTransportationFee =
                    isHomeVisitOrder &&
                    hasHomeVisit &&
                    !transportationFeeAdded &&
                    !(Number(orderDetails?.transportation_fee) > 0) &&
                    !orderDetails?.assigned_therapist_id;
                  const invoiceAlreadySent = orderDetails?.status && orderDetails?.status !== 'REQUEST_SUBMITTED' && (orderDetails?.scheduled_date || orderDetails?.scheduled_time);

                  // After payment done or COD: show Assign Therapist (home visit) or Start Processing (on-site)
                  // For assessment_required + payment done: show "Send Link to Therapist" button
                  if (isOrderAccepted && (isCODPayment || isPaymentPaid)) {
                    if (assessmentMode && orderDetails?.assigned_therapist_id) {
                      // Assessment mode + payment done + therapist already assigned: show Send Link to Therapist
                      return (
                        <div className="d-flex justify-content-center gap-3">
                          <button
                            className={styles["apt-register-btn"]}
                            disabled={sendingTherapistLink || therapistLinkSent}
                            onClick={async () => {
                              setSendingTherapistLink(true);
                              try {
                                const token = sessionStorage.getItem("usr_1a2b3c");
                                const axiosInstance = axios.create({
                                  baseURL: process.env.REACT_APP_API_URL,
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                const res = await axiosInstance.post(`${APP_PATH.physioOrders}/${orderId}/send-therapist-link`);
                                if (res.data?.status && res.data?.data?.appointmentLink) {
                                  const link = res.data.data.appointmentLink;
                                  toast.success(`Therapist link generated and copied to clipboard`, { autoClose: 5000 });
                                  try {
                                    await navigator.clipboard.writeText(link);
                                  } catch (clipErr) {
                                    console.warn("Clipboard write failed:", clipErr);
                                  }
                                  setTherapistLinkSent(true);
                                } else {
                                  toast.error(res.data?.message || "Failed to generate link");
                                }
                              } catch (err) {
                                toast.error(err?.response?.data?.message || "Failed to generate link");
                              } finally {
                                setSendingTherapistLink(false);
                              }
                            }}
                            style={{ backgroundColor: '#099494', color: 'white', opacity: (sendingTherapistLink || therapistLinkSent) ? 0.6 : 1, cursor: (sendingTherapistLink || therapistLinkSent) ? 'not-allowed' : 'pointer' }}
                          >
                            <FaLocationArrow /> {sendingTherapistLink ? 'Sending...' : therapistLinkSent ? 'Link Sent ✓' : 'Send Link to Therapist'}
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div className="d-flex justify-content-center gap-3">
                        <button
                          className={styles["apt-register-btn"]}
                          onClick={() => setActiveTab(isOnSiteOrder ? "Processing" : "Therapist Detail")}
                          style={{ backgroundColor: '#10b981', color: 'white' }}
                        >
                          <FaLocationArrow /> {isOnSiteOrder ? 'Start Processing' : 'Assign Therapist'}
                        </button>
                      </div>
                    );
                  }

                  // Invoice already sent or post-split confirmation - hide the send invoice button
                  // But show it if service date/time are not set (e.g., after a split, invoice still needs to be sent)
                  // While the order is still in ASSESSMENT (home visit OR on-site) the
                  // invoice has NOT been sent yet — sending it moves the order to
                  // AWAITING_PAYMENT — so never treat an ASSESSMENT order as "already
                  // sent". The scheduled_date/time may already be set from the
                  // assessment step, which is why the plain heuristic misfired here.
                  const isAssessmentOnSite = orderDetails?.status === 'ASSESSMENT' && isOnSiteLocation;
                  if ((invoiceAlreadySent || isPostSplitConfirmation) && (orderDetails?.scheduled_date || orderDetails?.scheduled_time) && orderDetails?.status !== 'ASSESSMENT') {
                    return null;
                  }

                  // Invoice not yet sent - show Send Invoice button or Assign Therapist button based on assessment mode
                  // If assessment mode is on AND therapist has already updated sessions, show Send Invoice
                  // Also show Send Invoice when status is ASSESSMENT and location is on-site (assessment order was accepted)
                  const sessionsUpdated = orderDetails?.sessions_updated_by_therapist === true;
                  const showAssignTherapist = assessmentMode && !orderDetails?.assigned_therapist_id && !sessionsUpdated && isHomeVisitOrder;
                  const showAcceptOrder = assessmentMode && !sessionsUpdated && isOnSiteOrder && orderDetails?.status === 'REQUEST_SUBMITTED';
                  const showSendInvoice = !assessmentMode || sessionsUpdated || isAssessmentOnSite;

                  return (
                    <>
                      {needsTransportationFee && (showSendInvoice || showAssignTherapist) && (
                        <div style={{ fontSize: '13px', color: '#F59E0B', marginBottom: '4px' }}>
                          Please add a transportation fee for home visit services before {showAssignTherapist ? 'assigning a therapist' : 'sending the invoice'}.
                        </div>
                      )}
                      <div className="d-flex justify-content-center gap-3">
                        {showAcceptOrder ? (
                          <button
                            className={styles["apt-register-btn"]}
                            onClick={async () => {
                              // Validate service date and time
                              if (!serviceDate || !(serviceDate instanceof Date) || isNaN(serviceDate.getTime())) {
                                setValidationErrors(prev => ({ ...prev, serviceDate: true }));
                                toast.error("Please select a service date before accepting the order");
                                document.getElementById('service-date-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                return;
                              }
                              if (blockServiceDateBeforePreferred()) return;
                              if (!serviceTime || serviceTime.trim() === "") {
                                setValidationErrors(prev => ({ ...prev, serviceTime: true }));
                                toast.error("Please select a service time before accepting the order");
                                document.getElementById('service-time-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                return;
                              }
                              // Update status to ASSESSMENT with date/time
                              try {
                                const token = sessionStorage.getItem("usr_1a2b3c");
                                const axiosInstance = axios.create({
                                  baseURL: process.env.REACT_APP_API_URL,
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                const formattedDate = `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}-${String(serviceDate.getDate()).padStart(2, '0')}`;
                                const res = await axiosInstance.put(`${APP_PATH.physioOrders}/${orderId}/status`, {
                                  status: 'ASSESSMENT',
                                  scheduledDate: formattedDate,
                                  scheduledTime: serviceTime,
                                });
                                if (res.data?.status) {
                                  toast.success("Order accepted for assessment");
                                  fetchOrderDetails();
                                } else {
                                  toast.error(res.data?.message || "Failed to accept order");
                                }
                              } catch (err) {
                                toast.error(err?.response?.data?.message || "Failed to accept order");
                              }
                            }}
                            style={{ backgroundColor: '#10b981', color: 'white' }}
                          >
                            <FaLocationArrow /> Accept Order
                          </button>
                        ) : showAssignTherapist ? (
                          <button
                            className={styles["apt-register-btn"]}
                            onClick={() => {
                              // Validate service date and time before allowing therapist assignment
                              if (!serviceDate || !(serviceDate instanceof Date) || isNaN(serviceDate.getTime())) {
                                setValidationErrors(prev => ({ ...prev, serviceDate: true }));
                                toast.error("Please select a service date before assigning a therapist");
                                document.getElementById('service-date-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                return;
                              }
                              if (blockServiceDateBeforePreferred()) return;
                              if (!serviceTime || serviceTime.trim() === "") {
                                setValidationErrors(prev => ({ ...prev, serviceTime: true }));
                                toast.error("Please select a service time before assigning a therapist");
                                document.getElementById('service-time-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                return;
                              }
                              setActiveTab("Therapist Detail");
                            }}
                            disabled={needsTransportationFee}
                            style={{ backgroundColor: '#099494', color: 'white', opacity: needsTransportationFee ? 0.5 : 1, cursor: needsTransportationFee ? 'not-allowed' : 'pointer' }}
                          >
                            <FaLocationArrow /> Assign Therapist
                          </button>
                        ) : showSendInvoice ? (
                          <button
                            className={styles["apt-register-btn"]}
                            onClick={handleSendInvoice}
                            disabled={needsTransportationFee || !services.length}
                            style={{ opacity: (needsTransportationFee || !services.length) ? 0.5 : 1, cursor: (needsTransportationFee || !services.length) ? 'not-allowed' : 'pointer' }}
                          >
                            <FaLocationArrow /> Send Invoice to Patient
                          </button>
                        ) : (
                          <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                            Waiting for therapist to complete assessment and add sessions...
                          </p>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* Accept Order button hidden - not needed
                <button className={styles["apt-register-btn"]} onClick={() => {}}>
                  Accept Order
                </button>
                */}
              </div>
            </div>
            {/* <div className={styles["action-buttons"]}>
              <button className={`${styles["btn"]} ${styles["btn-reject"]}`}>
                Reject
              </button>

              <button className={`${styles["btn"]} ${styles["btn-accept"]}`}>
                <FaCheck className={styles["icon"]} />
                Accept
              </button>
            </div>
            <div className={`text-end mt-4 `}>
              <button className={`${styles["apt-register-btn"]}  py-2 px-3`}>
                <Check size={16} /> Mark as Completed
              </button>
            </div> */}
          </>
        )}

        {(activeTab === "Therapist Detail" || activeTab === "Processing") && (
          <div className={`${styles["patient-tab-content"]} p-0 bg-white`}>
            <div className={styles["white-card"]}>
              {(() => {
                const isCOD = orderDetails?.payment_method?.toUpperCase() === 'COD';
                const isHomeVisit = isHomeVisitService;
                const isDispatched = ['THERAPIST_DISPATCHED', 'IN_PROGRESS', 'COMPLETED'].includes(orderDetails?.status);
                const isPaymentCollected = orderDetails?.payment_status === 'PAID' || orderDetails?.payment_status === 'COMPLETED';
                const isCompleted = orderDetails?.status === 'COMPLETED';

                return (
                  <>
                    {/* Scheduling & Dispatch Section */}
                    <div className={styles["process-dispatch-section"]}>
                      <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]}`}>
                        <h2>Scheduling & Dispatch</h2>
                      </div>

                      <div>
                        <div className={`${styles["patient-info-card"]} mt-3`}>
                          <div className={`${styles["dispatch-grid"]} pb-0`}>
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>Service Date</div>
                              <div className={styles["value"]}>
                                {(() => {
                                  const dateStr = orderDetails?.scheduled_date || orderDetails?.preferred_date;
                                  if (!dateStr) return 'Not scheduled';
                                  const parts = dateStr.split('-');
                                  if (parts.length === 3) {
                                    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                                    return tzLocale(d, 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                  }
                                  return tzCustom(new Date(dateStr), { day: '2-digit', month: '2-digit', year: 'numeric' });
                                })()}
                              </div>
                            </div>
                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>Service Time</div>
                              <div className={styles["value"]}>
                                {(() => {
                                  const time = orderDetails?.scheduled_time;
                                  if (!time) return orderDetails?.preferred_time_slot || 'Not scheduled';
                                  const parts = time.split(':');
                                  if (parts.length >= 2) {
                                    const h = parseInt(parts[0]);
                                    const m = parts[1];
                                    const period = h >= 12 ? 'PM' : 'AM';
                                    const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                    return `${dh}:${m} ${period}`;
                                  }
                                  return time;
                                })()}
                              </div>
                            </div>

                            <div className={styles["field-block"]}>
                              <div className={styles["label"]}>Service Location</div>
                              <div className={styles["value"]}>
                                <span style={{
                                  padding: '6px 16px',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  backgroundColor: orderDetails?.preferred_location === 'HOME_VISIT' ? '#E8F0FF' : '#F3E8FF',
                                  color: orderDetails?.preferred_location === 'HOME_VISIT' ? '#2563eb' : '#7c3aed',
                                }}>
                                  {orderDetails?.preferred_location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site'}
                                </span>
                              </div>
                            </div>

                            {/* Gender selection and therapist assignment - only for Home Visit */}
                            {isHomeVisit && (
                              <>
                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>Therapist Gender</div>
                                  <div className={styles["gender-options"]} style={orderDetails?.assigned_therapist_id ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                                    <label className={styles["gender-option"]}>
                                      <input
                                        type="checkbox"
                                        checked={gender === "male"}
                                        onChange={() => setGender("male")}
                                        disabled={!!orderDetails?.assigned_therapist_id}
                                      />
                                      <IoIosCheckbox className={styles["checkbox-icon"]} size={24} />
                                      Male
                                    </label>
                                    <label className={styles["gender-option"]}>
                                      <input
                                        type="checkbox"
                                        checked={gender === "female"}
                                        onChange={() => setGender("female")}
                                        disabled={!!orderDetails?.assigned_therapist_id}
                                      />
                                      <IoIosCheckbox className={styles["checkbox-icon"]} size={24} />
                                      Female
                                    </label>
                                  </div>
                                </div>

                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>Preferred Therapist Gender</div>
                                  <div className={styles["value"]}>
                                    {(() => {
                                      const g = orderDetails?.preferred_therapist_gender?.toUpperCase();
                                      const isMale = g === 'MALE';
                                      const isFemale = g === 'FEMALE';
                                      return (
                                        <span style={{
                                          padding: '6px 16px',
                                          borderRadius: '8px',
                                          fontSize: '14px',
                                          fontWeight: 500,
                                          backgroundColor: isMale ? '#DBEAFE' : isFemale ? '#FCE7F3' : '#F3F4F6',
                                          color: isMale ? '#1E40AF' : isFemale ? '#BE185D' : '#374151',
                                        }}>
                                          {g || 'Any'}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                </div>

                                <div className={styles["field-block"]}>
                                  <div className={styles["label"]}>Assigned Therapist</div>
                                  <div className={styles["value"]}>
                                    {orderDetails?.assigned_therapist_id
                                      ? (therapistsList.find(t => t.id === orderDetails.assigned_therapist_id)?.name || orderDetails?.therapist_name || `Therapist #${orderDetails.assigned_therapist_id}`)
                                      : 'Not assigned'}
                                  </div>
                                </div>

                                <div className={`${styles["field-block"]}`}>
                                  <div className={styles["label"]}>Assign Therapist</div>
                                  <div className={styles["dispatch-select-wrapper"]}>
                                    <select
                                      className={styles["dispatch-select"]}
                                      value={selectedTherapistId}
                                      onChange={(e) => setSelectedTherapistId(e.target.value)}
                                      disabled={
                                        !gender ||
                                        orderDetails?.assigned_therapist_id ||
                                        (!assessmentMode && orderDetails?.status !== 'ORDER_ACCEPTED' && orderDetails?.status !== 'THERAPIST_ASSIGNED')
                                      }
                                    >
                                      <option value="">
                                        {orderDetails?.assigned_therapist_id
                                          ? (therapistsList.find(t => t.id === orderDetails.assigned_therapist_id)?.name || "Therapist assigned")
                                          : !gender
                                            ? "Please select therapist gender"
                                            : "Select therapist"}
                                      </option>
                                      {therapistsList
                                        .filter((t) => gender ? t.gender?.toLowerCase() === gender.toLowerCase() : true)
                                        .map((t) => (
                                          <option key={t.id} value={t.id}>
                                            {t.name} ({t.gender})
                                          </option>
                                        ))}
                                    </select>
                                    <svg className={styles["select-arrow"]} width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M1 1.5L6 6.5L11 1.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </div>
                                </div>

                              </>
                            )}
                          </div>
                        </div>

                        {/* Assign Therapist Button */}
                        {isHomeVisit && !orderDetails?.assigned_therapist_id && (
                          <div style={{ padding: '4px 0 16px', display: 'flex' }}>
                            <div style={{ flex: '1' }}></div>
                            <div style={{ flex: '1', paddingLeft: '8px' }}>
                              <button
                                className={styles["apt-register-btn"]}
                                style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}
                                onClick={handleAssignTherapist}
                                // No "!selectedTherapistId" here on purpose: the click must reach
                                // handleAssignTherapist so it can tell the officer to pick a
                                // therapist. Still disabled while the therapist dropdown itself is
                                // locked by the order status (same rule as the select above).
                                disabled={
                                  orderDetails?.assigned_therapist_id ||
                                  (!assessmentMode && orderDetails?.status !== 'ORDER_ACCEPTED' && orderDetails?.status !== 'THERAPIST_ASSIGNED')
                                }
                              >
                                <FaLocationArrow /> Assign Therapist
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Home Visit: Payment collection and completion is handled via public therapist page */}

                    {/* On-Site: Payment Collection (COD) and Complete Order */}
                    {!isHomeVisit && (
                    <div className={styles["medical-history"]}>
                      {/* Payment Collection - Only for COD */}
                      {isCOD && (
                        <>
                          <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}>
                            <h2>Payment Collection</h2>
                          </div>
                          <button
                            className={`${styles["apt-register-btn"]} ${isPaymentCollected ? styles["text-green-info"] : ""} py-2 px-3`}
                            onClick={async () => {
                              if (isPaymentCollected || collectingPayment) return;
                              setCollectingPayment(true);
                              try {
                                const token = sessionStorage.getItem("usr_1a2b3c");
                                const axiosInstance = axios.create({
                                  baseURL: process.env.REACT_APP_API_URL,
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                const res = await axiosInstance.put(`${APP_PATH.physioOrders}/${orderId}/status`, { status: 'COMPLETED' });
                                if (res.data?.status) {
                                  toast.success("Payment collected and order completed");
                                  fetchOrderDetails();
                                } else {
                                  toast.error(res.data?.message || "Failed to collect payment");
                                }
                              } catch (e) {
                                toast.error(e?.response?.data?.message || "Failed to collect payment");
                              } finally {
                                setCollectingPayment(false);
                              }
                            }}
                            disabled={isPaymentCollected || collectingPayment}
                          >
                            <Check size={16} /> {collectingPayment ? 'Processing...' : isPaymentCollected ? 'Cash Collected ✓' : 'Mark as Collected'}
                          </button>
                        </>
                      )}

                      {/* Complete Order - Only for Wallet/Card (non-COD) */}
                      {!isCOD && (
                        <>
                          <div className={`${styles["patient-tab-title"]} ${styles["med-tab-title"]} border-0`}>
                            <h2>Complete Order</h2>
                          </div>
                          <button
                            className={`${styles["apt-register-btn"]} ${isCompleted ? styles["text-green-info"] : ""} py-2 px-3`}
                            onClick={async () => {
                              if (isCompleted || collectingPayment) return;
                              setCollectingPayment(true);
                              try {
                                const token = sessionStorage.getItem("usr_1a2b3c");
                                const axiosInstance = axios.create({
                                  baseURL: process.env.REACT_APP_API_URL,
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                const res = await axiosInstance.put(`${APP_PATH.physioOrders}/${orderId}/status`, { status: 'COMPLETED' });
                                if (res.data?.status) {
                                  toast.success("Order marked as completed");
                                  fetchOrderDetails();
                                } else {
                                  toast.error(res.data?.message || "Failed to complete order");
                                }
                              } catch (e) {
                                toast.error(e?.response?.data?.message || "Failed to complete order");
                              } finally {
                                setCollectingPayment(false);
                              }
                            }}
                            disabled={isCompleted || collectingPayment}
                          >
                            <Check size={16} /> {collectingPayment ? 'Processing...' : isCompleted ? 'Order Completed ✓' : 'Mark as Completed'}
                          </button>
                        </>
                      )}
                    </div>
                    )}

                  </>
                );
              })()}
            </div>
          </div>
        )}

        {activeTab === "Results & Reports" && (
          <>
            <div className={`${styles["patient-tab-content"]} p-0`}>
              <div className={`${styles["white-card"]} m-0 position-relative`}>
                {reportSent && (
                  <div
                    className={styles["report-sent-notice"]}
                    style={{
                      backgroundColor: "#e8f5e8",
                      border: "1px solid #4caf50",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      marginBottom: "16px",
                      color: "#2e7d32",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    <Check
                      size={16}
                      style={{ marginRight: "8px", verticalAlign: "middle" }}
                    />
                    Report has been shared with the patient. Upload, sharing, and
                    file deletion are disabled.
                  </div>
                )}

                <input
                  type="file"
                  id="physio-file-upload"
                  className={styles["file-input"]}
                  accept="application/pdf"
                  onChange={handleUploadResults}
                  disabled={uploadingResult || reportSent}
                />
                <label
                  className={`${styles["result-upload-card"]} ${uploadingResult ? styles["uploading"] : ""} ${reportSent ? styles["disabled"] : ""}`}
                  htmlFor="physio-file-upload"
                  style={{
                    opacity: reportSent ? 0.6 : 1,
                    cursor: reportSent ? "not-allowed" : "pointer",
                    pointerEvents: reportSent ? "none" : "auto",
                  }}
                >
                  <h2>Upload Reports</h2>
                  <p className={styles["result-subtitle"]}>
                    {reportSent
                      ? "Report has been shared with the patient. Upload is disabled."
                      : "Upload the final PDF report(s). Each upload is added separately, so you can attach multiple reports."}
                  </p>
                  <span className={styles["result-upload-btn"]}>
                    <Upload size={20} />{" "}
                    {reportSent
                      ? "Upload Disabled"
                      : uploadingResult
                        ? "Uploading..."
                        : "Upload"}
                  </span>
                </label>

                <div className={`${styles["result-file-list"]} mt-4`}>
                  {testResults.length === 0 ? (
                    <p style={{ color: "#888", padding: "8px 0" }}>
                      No reports uploaded yet.
                    </p>
                  ) : (
                    testResults.map((doc, i) => (
                      <div
                        key={doc.id || i}
                        className={styles["result-file-item"]}
                      >
                        <span className={styles["result-file-icon"]}>
                          <AiOutlineFilePdf size={20} color="#E55353" />
                        </span>
                        <span
                          className={styles["result-file-name"]}
                          onClick={() => handleResultFileClick(doc.id)}
                          style={{
                            cursor: "pointer",
                            color: "#155DFC",
                            textDecoration: "underline",
                          }}
                        >
                          {doc.file_name || `Report ${i + 1}.pdf`}
                        </span>
                        <button
                          className={styles["result-delete-btn"]}
                          onClick={() =>
                            handleResultFileDelete(doc.id, doc.file_name)
                          }
                          disabled={reportSent}
                          style={{
                            opacity: reportSent ? 0.4 : 1,
                            cursor: reportSent ? "not-allowed" : "pointer",
                          }}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className={styles["result-actions"]}>
                  <button
                    className={styles["result-share-btn"]}
                    onClick={handleShareWithPatient}
                    disabled={reportSent || testResults.length === 0}
                    style={{
                      opacity: reportSent || testResults.length === 0 ? 0.6 : 1,
                      cursor:
                        reportSent || testResults.length === 0
                          ? "not-allowed"
                          : "pointer",
                      backgroundColor: reportSent ? "#ccc" : "",
                    }}
                  >
                    <Check size={16} />{" "}
                    {reportSent ? "Already Shared" : "Share with Patient & Doctor"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {/* Invoice Modal */}
      {/* Invoice — the SHARED PhysioInvoiceModal, the same component used by the Order
          Management table and the Dashboard, so every place that prints an invoice renders
          identical content and fee math (loyalty discount included). This page previously
          carried its OWN copy of the invoice, which is why the loyalty row could appear in
          one place and not the other. The modal fetches the order itself from the id. */}
      <PhysioInvoiceModal
        open={showInvoiceModal}
        orderId={orderId}
        onClose={() => setShowInvoiceModal(false)}
      />
    </PhysioDashboardLayout>
  );
};

export default PhysioAppointmentDetail;
